/* Internal Chat — self-contained dock mounted in both Notes & Tasks shells.
 * Layout "Option A" (Split Inbox): online-only presence strip (excluding people
 * you already have a conversation with) → conversation list → inline thread.
 * Real-time via a dedicated chat WebSocket; Web Push handled server-side.
 * Read = green bubble border, Unread = red (on both sent + incoming).
 * Features: presence, DMs + groups, New-chat (any user), reply, delete,
 * in-chat search, and sharing a task/note as a card (only when every member
 * already has access). */
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare, Search, Users, Plus, X, Send, ArrowLeft, ArrowDownLeft, Check, CheckCheck,
  Loader2, UserPlus, ChevronLeft, SquarePen, CornerUpLeft, Trash2, Paperclip, Pin,
  ChevronUp, ChevronDown, FileText, CheckSquare, MoreVertical, Copy, Clock, ChevronRight,
  Smile, SmilePlus, BarChart3, Bold, Italic, Strikethrough, Code, PanelRight, PanelRightClose, AlertTriangle, Palette, Check as CheckIcon,
} from 'lucide-react';
import EmojiPicker, { Theme as EmojiTheme } from 'emoji-picker-react';
import { notesApi } from '@/lib/notesApi';
import { useNotesAuth } from '@/lib/notesAuth';
import { CHAT_BG_OPTIONS, chatBgStyle, chatBgSwatchStyle, getDefaultBg, setDefaultBg as persistDefaultBg, readBgMap, writeBgMap } from './chatBackgrounds';

const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '👎'];

const HUES = ['#6C8FD1', '#C77D57', '#6FA8A0', '#B072B0', '#B8892E', '#7C9A5B', '#C98BA0', '#5B8D8A'];
const hueFor = (id) => {
  const s = String(id || '');
  let h = 0; for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return HUES[h % HUES.length];
};
const initialOf = (name) => (name || 'U').trim().charAt(0).toUpperCase();

const fmtTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }); } catch { return ''; }
};
const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso); const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return fmtTime(iso);
  const diff = (now - d) / 86400000;
  if (diff < 7) return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};
// Same calendar day (local time) — used to group messages under a date divider.
const sameCalDay = (a, b) => {
  if (!a || !b) return false;
  const da = new Date(a); const db = new Date(b);
  return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth() && da.getDate() === db.getDate();
};
// WhatsApp-style date label for the divider bar.
const fmtDateDivider = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso); const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const that = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const diffDays = Math.round((today - that) / 86400000);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays > 1 && diffDays < 7) return d.toLocaleDateString([], { weekday: 'long' });
    const opts = { day: 'numeric', month: 'long' };
    if (d.getFullYear() !== now.getFullYear()) opts.year = 'numeric';
    return d.toLocaleDateString([], opts);
  } catch { return ''; }
};
const fmtLastSeen = (iso) => {
  if (!iso) return 'offline';
  const d = new Date(iso); const now = new Date();
  const mins = Math.floor((now - d) / 60000);
  if (mins < 1) return 'last seen just now';
  if (mins < 60) return `last seen ${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `last seen ${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'last seen yesterday';
  if (days < 7) return `last seen ${days}d ago`;
  return `last seen ${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}`;
};

const fmtDue = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' }); } catch { return ''; }
};
const STATUS_META = {
  todo: { label: 'To do', color: 'var(--n-muted)' },
  in_progress: { label: 'In progress', color: 'var(--n-accent)' },
  doing: { label: 'In progress', color: 'var(--n-accent)' },
  blocked: { label: 'Blocked', color: 'var(--n-danger)' },
  done: { label: 'Done', color: 'var(--n-success)' },
  completed: { label: 'Done', color: 'var(--n-success)' },
};
const statusMeta = (s, completed) => {
  if (completed) return STATUS_META.done;
  return STATUS_META[s] || { label: (s || 'To do').replace(/_/g, ' '), color: 'var(--n-muted)' };
};

// highlight all occurrences of q within text -> React nodes
const highlightText = (text, q) => {
  const s = text || '';
  const ql = (q || '').trim().toLowerCase();
  if (!ql) return s;
  const lower = s.toLowerCase();
  const parts = []; let i = 0; let key = 0;
  let pos = lower.indexOf(ql, i);
  if (pos === -1) return s;
  while (pos !== -1) {
    if (pos > i) parts.push(s.slice(i, pos));
    parts.push(
      <mark key={key} style={{ background: 'var(--n-accent-soft)', color: 'inherit', borderRadius: 3, padding: '0 1px' }}>
        {s.slice(pos, pos + ql.length)}
      </mark>);
    key += 1;
    i = pos + ql.length;
    pos = lower.indexOf(ql, i);
  }
  if (i < s.length) parts.push(s.slice(i));
  return parts;
};

// Inline formatting: *bold*, _italic_, ~strike~, `code`. Renders to React nodes
// and applies the search highlight inside the plain-text pieces. Non-nested.
const INLINE_RE = /(\*[^*\s][^*]*?\*|_[^_\s][^_]*?_|~[^~\s][^~]*?~|`[^`]+?`)/g;
const renderRich = (text, q) => {
  const s = text || '';
  if (!s) return s;
  const nodes = []; let last = 0; let key = 0; let m;
  INLINE_RE.lastIndex = 0;
  while ((m = INLINE_RE.exec(s)) !== null) {
    const tok = m[0]; const start = m.index;
    if (start > last) nodes.push(<React.Fragment key={`t${key}`}>{highlightText(s.slice(last, start), q)}</React.Fragment>);
    const inner = highlightText(tok.slice(1, -1), q); const ch = tok[0];
    if (ch === '*') nodes.push(<strong key={`f${key}`} style={{ fontWeight: 700 }}>{inner}</strong>);
    else if (ch === '_') nodes.push(<em key={`f${key}`}>{inner}</em>);
    else if (ch === '~') nodes.push(<span key={`f${key}`} style={{ textDecoration: 'line-through' }}>{inner}</span>);
    else nodes.push(<code key={`f${key}`} style={{ fontFamily: "'Source Code Pro', ui-monospace, monospace", fontSize: '0.9em', background: 'var(--n-canvas-2)', borderRadius: 4, padding: '0 4px' }}>{inner}</code>);
    last = start + tok.length; key += 1;
  }
  if (last < s.length) nodes.push(<React.Fragment key={`t${key}`}>{highlightText(s.slice(last), q)}</React.Fragment>);
  return nodes.length ? nodes : s;
};

const reactionTitle = (r, members, me) => {
  const names = (r.user_ids || []).map((uid) => (uid === me ? 'You' : ((members[uid] && members[uid].name) || 'Someone')));
  return names.join(', ');
};

const Avatar = ({ name, id, size = 38, group, avatar }) => (
  <span className="relative inline-grid shrink-0 place-items-center rounded-full font-bold text-white"
    style={{ width: size, height: size, background: hueFor(id), fontSize: avatar ? size * 0.5 : size * 0.4 }}>
    {avatar ? <span style={{ lineHeight: 1 }}>{avatar}</span> : (group ? <Users size={size * 0.5} /> : initialOf(name))}
  </span>
);
const Dot = ({ online, hidden }) => (hidden ? null : (
  <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2"
    style={{ background: online ? 'var(--n-success)' : 'var(--n-muted-2)', borderColor: 'var(--n-surface)' }} />
));
const CountBadge = ({ n, danger }) => (n > 0 ? (
  <span className="grid h-5 min-w-5 place-items-center rounded-full px-1.5 text-[11px] font-bold text-white"
    style={{ background: danger ? 'var(--n-danger)' : 'var(--n-accent)' }} data-testid="chat-conversation-unread-count">{n > 99 ? '99+' : n}</span>
) : null);

// -- shared task/note card (with live status + due preview for tasks) ------
const CardBody = ({ card, query, onOpenCard }) => {
  const isTask = card.type === 'task';
  const st = isTask ? statusMeta(card.status, card.is_completed) : null;
  return (
    <button type="button" onClick={() => onOpenCard(card)} data-testid="chat-card-bubble"
      className="block w-full rounded-lg border px-2 py-1.5 text-left transition-colors"
      style={{ borderColor: 'var(--n-border)', background: 'var(--n-surface)' }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--n-canvas-2)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--n-surface)'; }}>
      <span className="flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wide" style={{ color: 'var(--n-accent)' }}>
        {isTask ? <CheckSquare size={12} /> : <FileText size={12} />}{isTask ? 'Task' : 'Note'}
      </span>
      <span className="mt-0.5 block text-[13px] font-medium" style={{ color: 'var(--n-ink)' }}>{highlightText(card.title, query)}</span>
      {card.subtitle && <span className="mt-0.5 block truncate text-[11px]" style={{ color: 'var(--n-muted)' }}>{card.subtitle}</span>}
      {isTask && (st || card.due_at || card.priority) && (
        <span className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid="chat-card-preview">
          {st && (
            <span className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold" style={{ background: 'var(--n-canvas-2)', color: st.color }}>
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: st.color }} />{st.label}
            </span>
          )}
          {card.priority && (
            <span className="inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold capitalize" style={{ background: 'var(--n-canvas-2)', color: 'var(--n-muted)' }}>{card.priority}</span>
          )}
          {card.due_at && (
            <span className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold" style={{ background: 'var(--n-canvas-2)', color: 'var(--n-muted)' }}>
              <Clock size={10} />{fmtDue(card.due_at)}
            </span>
          )}
        </span>
      )}
    </button>
  );
};

// -- WhatsApp-style delivery/read receipt (shared by thread bubble + list row) --
// Returns 'sent' | 'delivered' | 'read' for a message the current user sent.
// Matches the thread's ".every()" semantics so DMs and groups behave identically.
const computeReceipt = (memberList, senderId, at) => {
  if (!at) return 'sent';
  const others = (memberList || []).filter((mem) => mem && mem.user_id !== senderId);
  if (others.length === 0) return 'sent';
  if (others.every((mem) => mem.last_read_at && mem.last_read_at >= at)) return 'read';
  if (others.every((mem) => mem.last_delivered_at && mem.last_delivered_at >= at)) return 'delivered';
  return 'sent';
};

// Single grey tick (sent), double grey tick (delivered), double green tick (read).
const Receipt = ({ state, size = 14 }) => {
  if (state === 'read') return <CheckCheck size={size} style={{ color: 'var(--n-success)' }} />;
  if (state === 'delivered') return <CheckCheck size={size} style={{ color: 'var(--n-muted-2)' }} />;
  return <Check size={size} style={{ color: 'var(--n-muted-2)' }} />;
};

// -- "Seen by" for own messages in group chats (inline, no clipping) --------
const SeenBy = ({ m, members }) => {
  const [open, setOpen] = useState(false);
  const readers = Object.entries(members)
    .filter(([uid, mem]) => uid !== m.sender_id && mem.last_read_at && mem.last_read_at >= m.created_at)
    .map(([, mem]) => mem.name || 'User');
  const others = Object.keys(members).filter((uid) => uid !== m.sender_id).length;
  const label = readers.length === 0 ? 'Sent' : (others > 0 && readers.length >= others ? 'Seen by all' : `Seen by ${readers.length}`);
  return (
    <span className="mt-1 block text-right">
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid="chat-seen-by"
        className="inline-flex items-center gap-0.5 text-[10px] font-medium" style={{ color: 'var(--n-muted)' }}>
        {label}{readers.length > 0 && (open ? <ChevronDown size={11} /> : <ChevronRight size={11} />)}
      </button>
      {open && (
        <span className="mt-1 block rounded-md p-1.5 text-left" style={{ background: 'var(--n-canvas-2)' }} data-testid="chat-seen-by-popover">
          {readers.length === 0 ? (
            <span className="block text-[11px]" style={{ color: 'var(--n-muted)' }}>No one has read this yet</span>
          ) : readers.map((n) => (
            <span key={n} className="flex items-center gap-1.5 py-0.5 text-[11.5px]" style={{ color: 'var(--n-ink)' }}>
              <CheckCheck size={12} style={{ color: 'var(--n-success)' }} />{n}
            </span>
          ))}
        </span>
      )}
    </span>
  );
};

// -- reaction chips shown under a message ----------------------------------
const ReactionChips = ({ m, members, me, onReact }) => {
  if (!m.reactions || m.reactions.length === 0) return null;
  return (
    <div className={`mt-1 flex flex-wrap gap-1 ${m.mine ? 'justify-end' : 'justify-start'}`} data-testid="chat-reactions">
      {m.reactions.map((r) => (
        <button key={r.emoji} type="button" onClick={() => onReact(m, r.emoji)} data-testid="chat-reaction-chip"
          title={reactionTitle(r, members, me)}
          className="inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[12px] leading-none transition-colors"
          style={{ borderColor: r.mine ? 'var(--n-accent)' : 'var(--n-border)', background: r.mine ? 'var(--n-accent-bg)' : 'var(--n-surface)', color: 'var(--n-ink)' }}>
          <span>{r.emoji}</span>
          <span className="tabular-nums font-semibold" style={{ color: r.mine ? 'var(--n-accent)' : 'var(--n-muted)' }}>{r.count}</span>
        </button>
      ))}
    </div>
  );
};

// -- poll card (distinct from a normal message) ----------------------------
const PollCard = ({ m, poll, members, me, onVote, onClosePoll }) => {
  const total = poll.total_votes || 0;
  return (
    <div className="w-[min(300px,76vw)]" data-testid="chat-poll-card">
      <span className="flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wide" style={{ color: 'var(--n-accent)' }}>
        <BarChart3 size={12} /> Poll{poll.multi ? ' · multiple choice' : ''}{poll.closed ? ' · closed' : ''}
      </span>
      <span className="mt-1 block text-[14px] font-semibold" style={{ color: 'var(--n-ink)' }} data-testid="chat-poll-question">{poll.question}</span>
      <div className="mt-2 flex flex-col gap-1.5">
        {poll.options.map((o) => {
          const pct = total ? Math.round((o.count / total) * 100) : 0;
          const voters = (o.voter_ids || []).map((uid) => (uid === me ? 'You' : ((members[uid] && members[uid].name) || 'Someone')));
          return (
            <button key={o.id} type="button" disabled={poll.closed} onClick={() => onVote(m, o.id)} data-testid="chat-poll-option"
              className="relative block w-full overflow-hidden rounded-lg border px-2.5 py-2 text-left transition-colors disabled:cursor-default"
              style={{ borderColor: o.voted ? 'var(--n-accent)' : 'var(--n-border)', background: 'var(--n-surface)', minHeight: 46 }}>
              <span className="absolute inset-y-0 left-0" style={{ width: `${pct}%`, background: 'var(--n-accent-bg)', transition: 'width 220ms ease' }} />
              <span className="relative flex items-center gap-2">
                <span className="grid h-4 w-4 shrink-0 place-items-center rounded-full border" style={{ borderColor: o.voted ? 'var(--n-accent)' : 'var(--n-border)', background: o.voted ? 'var(--n-accent)' : 'transparent' }}>
                  {o.voted && <Check size={11} style={{ color: '#fff' }} />}
                </span>
                <span className="min-w-0 flex-1 text-[13px]" style={{ color: 'var(--n-ink)' }}>{o.text}</span>
                <span className="shrink-0 text-[11px] font-semibold tabular-nums" style={{ color: 'var(--n-muted)' }} data-testid="chat-poll-option-stat">{pct}% · {o.count}</span>
              </span>
              {voters.length > 0 && (
                <span className="relative mt-1 block truncate text-[10px]" style={{ color: 'var(--n-muted-2)' }}>{voters.join(', ')}</span>
              )}
            </button>
          );
        })}
      </div>
      <div className="mt-1.5 flex items-center justify-between text-[10.5px]" style={{ color: 'var(--n-muted)' }}>
        <span data-testid="chat-poll-voters">{poll.total_voters} voter{poll.total_voters === 1 ? '' : 's'}{poll.closed ? '' : ' · tap to vote'}</span>
        {m.mine && !poll.closed && (
          <button type="button" onClick={() => onClosePoll(m)} data-testid="chat-poll-close" className="font-semibold" style={{ color: 'var(--n-danger)' }}>End poll</button>
        )}
        {poll.closed && <span className="font-semibold" style={{ color: 'var(--n-muted-2)' }}>Final results</span>}
      </div>
    </div>
  );
};

// -- message bubble (right-click / long-press for the actions menu) --------
const Bubble = ({ m, read, state, query, activeMatch, isGroup, members, me, onOpenCard, onOpenMenu, onReact, onOpenReactPicker, onVote, onClosePoll }) => {
  const stripe = read ? 'var(--n-success)' : 'var(--n-danger)';
  const isPoll = m.kind === 'poll' && m.poll;
  const [quick, setQuick] = useState(null); // null | {x,y} anchor for the quick-reaction bar
  const touchTimer = useRef(null);
  const openMenu = (x, y) => { if (!m.deleted) onOpenMenu({ x, y }, m); };
  const onCtx = (e) => { if (m.deleted) return; e.preventDefault(); openMenu(e.clientX, e.clientY); };
  const onTouchStart = (e) => { const t = e.touches[0]; touchTimer.current = setTimeout(() => openMenu(t.clientX, t.clientY), 460); };
  const clearTouch = () => { if (touchTimer.current) { clearTimeout(touchTimer.current); touchTimer.current = null; } };

  const Actions = () => (m.deleted ? null : (
    <div className="mb-1 flex items-center gap-0.5">
      <button type="button" data-testid="chat-react-button" title="React"
        onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setQuick(quick ? null : { x: r.left, y: r.top }); }}
        className="grid h-6 w-6 shrink-0 place-items-center rounded-md opacity-0 transition-opacity md:group-hover/msg:opacity-100" style={{ color: 'var(--n-muted)' }}>
        <SmilePlus size={14} />
      </button>
      <button type="button" onClick={(e) => openMenu(e.clientX, e.clientY)} data-testid="chat-message-menu-button" title="More"
        className="grid h-6 w-6 shrink-0 place-items-center rounded-md opacity-0 transition-opacity md:group-hover/msg:opacity-100" style={{ color: 'var(--n-muted)' }}>
        <MoreVertical size={14} />
      </button>
      {quick && (
        <>
          <div className="fixed inset-0 z-[330]" onClick={() => setQuick(null)} />
          <div data-testid="chat-quick-reactions"
            className="fixed z-[331] flex items-center gap-0.5 rounded-full border px-1 py-0.5"
            style={{
              left: Math.max(8, Math.min(quick.x - 8, (typeof window !== 'undefined' ? window.innerWidth : 400) - 232)),
              top: quick.y > 52 ? quick.y - 44 : quick.y + 28,
              borderColor: 'var(--n-border)', background: 'var(--n-surface)', boxShadow: 'var(--n-shadow)',
            }}>
            {QUICK_REACTIONS.map((e) => (
              <button key={e} type="button" onClick={() => { onReact(m, e); setQuick(null); }} data-testid="chat-quick-reaction"
                className="grid h-8 w-8 place-items-center rounded-full text-[18px] leading-none transition-transform hover:scale-125">{e}</button>
            ))}
            <button type="button" onClick={() => { setQuick(null); onOpenReactPicker(m); }} data-testid="chat-quick-reaction-more"
              className="grid h-8 w-8 place-items-center rounded-full" style={{ color: 'var(--n-muted)' }}><Plus size={16} /></button>
          </div>
        </>
      )}
    </div>
  ));

  return (
    <div className={`group/msg flex w-full items-end gap-1 ${m.mine ? 'justify-end' : 'justify-start'}`} data-testid="chat-message-bubble">
      {m.mine && <Actions />}
      <div className="flex max-w-[85%] flex-col" onContextMenu={onCtx} onTouchStart={onTouchStart} onTouchEnd={clearTouch} onTouchMove={clearTouch}>
        <div className={`select-none overflow-hidden text-[13.5px] leading-relaxed ${isPoll ? 'rounded-xl border' : 'rounded-lg'}`}
          style={isPoll ? {
            borderColor: 'var(--n-border)', background: 'var(--n-surface)', color: 'var(--n-ink)',
            boxShadow: activeMatch ? '0 0 0 2px var(--n-accent)' : 'var(--n-shadow-sm, none)',
          } : {
            background: m.deleted ? 'transparent' : 'var(--n-surface-2)',
            color: m.deleted ? 'var(--n-muted)' : 'var(--n-ink)',
            borderLeft: `3px solid ${m.deleted ? 'var(--n-border)' : stripe}`,
            fontStyle: m.deleted ? 'italic' : 'normal',
            boxShadow: activeMatch ? '0 0 0 2px var(--n-accent)' : 'none',
          }}>
          <div className="px-3 py-2">
            {m.reply_to && !m.deleted && (
              <span className="mb-1.5 block rounded-md border-l-[3px] pl-2 pr-1.5 py-1" style={{ borderColor: 'var(--n-accent)', background: 'var(--n-surface)' }}>
                <span className="block text-[10.5px] font-semibold" style={{ color: 'var(--n-accent)' }}>{m.reply_to.sender_name || 'User'}</span>
                <span className="block truncate text-[11.5px]" style={{ color: 'var(--n-muted)' }}>{m.reply_to.text || 'Shared item'}</span>
              </span>
            )}
            {m.deleted ? 'This message was deleted' : (
              isPoll
                ? <PollCard m={m} poll={m.poll} members={members} me={me} onVote={onVote} onClosePoll={onClosePoll} />
                : (m.kind === 'card' && m.card)
                  ? <CardBody card={m.card} query={query} onOpenCard={onOpenCard} />
                  : renderRich(m.text, query)
            )}
            <span className="mt-1 flex items-center justify-end gap-1 text-[10.5px] tabular-nums" style={{ color: 'var(--n-muted)' }}>
              {fmtTime(m.created_at)}
              {m.mine && !m.deleted && <Receipt state={state} />}
            </span>
            {isGroup && m.mine && !m.deleted && <SeenBy m={m} members={members} />}
          </div>
        </div>
        <ReactionChips m={m} members={members} me={me} onReact={onReact} />
      </div>
      {!m.mine && <Actions />}
    </div>
  );
};

// Touch devices have no hover, so pin controls must stay visible there.
const NO_HOVER = typeof window !== 'undefined' && !!(window.matchMedia && window.matchMedia('(hover: none)').matches);

// Pinned conversations always float to the top (per-user), then by recency.
const sortConvs = (list) => [...(list || [])].sort((a, b) => {
  const pa = a.pinned ? 1 : 0, pb = b.pinned ? 1 : 0;
  if (pa !== pb) return pb - pa;
  return (b.last_message_at || '').localeCompare(a.last_message_at || '');
});

// -- conversation row -------------------------------------------------------
const ConvRow = ({ c, active, onClick, typingLabel, me, pinned, onTogglePin, canDelete, onRequestDelete }) => {
  const unread = c.unread > 0;
  const restBg = active ? 'var(--n-accent-bg)' : 'transparent';
  const isSystem = (c.last_kind || 'text') === 'system';
  const hasSender = !!c.last_sender_id;
  const mine = !!(me && hasSender && c.last_sender_id === me);
  const receipt = (mine && !isSystem) ? computeReceipt(c.members, me, c.last_message_at) : null;
  const showIncoming = hasSender && !mine && !isSystem;
  const onKey = (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } };
  return (
    <div role="button" tabIndex={0} onClick={onClick} onKeyDown={onKey} data-testid="chat-conversation-row" data-unread={unread ? 'true' : 'false'} data-pinned={pinned ? 'true' : 'false'}
      className="group flex w-full cursor-pointer items-center gap-3 rounded-xl px-2.5 py-2.5 text-left outline-none transition-[background-color,transform,box-shadow] duration-150 will-change-transform focus-visible:ring-2 focus-visible:ring-[var(--n-accent)]"
      style={{ background: restBg }}
      onMouseEnter={(e) => { const el = e.currentTarget; if (!active) el.style.background = 'var(--n-canvas-2)'; el.style.transform = 'translateY(-1px)'; el.style.boxShadow = '0 8px 18px -12px rgba(0,0,0,.55)'; }}
      onMouseLeave={(e) => { const el = e.currentTarget; el.style.background = restBg; el.style.transform = 'translateY(0)'; el.style.boxShadow = 'none'; }}>
      <span className="relative"><Avatar name={c.name} id={c.other_user_id || c.id} group={c.type === 'group'} avatar={c.avatar} /><Dot online={c.online} hidden={c.type === 'group'} /></span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          {unread && <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: 'var(--n-accent)', boxShadow: '0 0 0 3px color-mix(in srgb, var(--n-accent) 22%, transparent)' }} data-testid="chat-conversation-unread-dot" />}
          <span className={`truncate text-[13.5px] ${unread ? 'font-bold' : 'font-semibold'}`} style={{ color: 'var(--n-ink)' }}>{c.name}</span>
          <span className="ml-auto flex shrink-0 items-center gap-1">
            <button type="button" data-testid="chat-conversation-pin-toggle" data-pinned={pinned ? 'true' : 'false'}
              title={pinned ? 'Unpin conversation' : 'Pin conversation'} aria-label={pinned ? 'Unpin conversation' : 'Pin conversation'}
              onClick={(e) => { e.stopPropagation(); onTogglePin?.(c); }}
              className={`grid h-5 w-5 place-items-center rounded-md transition-opacity duration-150 hover:bg-[var(--n-canvas-2)] ${pinned || NO_HOVER ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100'}`}
              style={{ color: pinned ? 'var(--n-accent)' : 'var(--n-muted-2)' }}>
              <Pin size={13} fill={pinned ? 'var(--n-accent)' : 'none'} />
            </button>
            {canDelete && (
              <button type="button" data-testid="chat-conversation-delete"
                title="Delete chat" aria-label="Delete chat"
                onClick={(e) => { e.stopPropagation(); onRequestDelete?.(c); }}
                className={`grid h-5 w-5 place-items-center rounded-md transition-opacity duration-150 hover:bg-[var(--n-canvas-2)] ${NO_HOVER ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100'}`}
                style={{ color: 'var(--n-muted-2)' }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--n-danger)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--n-muted-2)'; }}>
                <Trash2 size={13} />
              </button>
            )}
            <span className="text-[11px] font-medium tabular-nums" style={{ color: unread ? 'var(--n-accent)' : 'var(--n-muted)' }}>{fmtWhen(c.last_message_at)}</span>
          </span>
        </span>
        <span className="mt-0.5 flex items-center gap-1.5">
          {typingLabel ? (
            <span className="n-typing-shimmer truncate text-[12px] font-semibold" data-testid="chat-conversation-typing">{typingLabel}</span>
          ) : (
            <>
              {mine && !isSystem && (
                <span className="shrink-0" data-testid="chat-conversation-receipt" data-receipt={receipt}><Receipt state={receipt} size={13} /></span>
              )}
              {showIncoming && (
                <ArrowDownLeft size={13} className="shrink-0" style={{ color: unread ? 'var(--n-accent)' : 'var(--n-muted-2)' }} data-testid="chat-conversation-incoming" />
              )}
              <span className={`truncate text-[12px] ${unread ? 'font-semibold' : ''}`} style={{ color: unread ? 'var(--n-ink-soft)' : 'var(--n-muted)' }}>{c.preview || 'No messages yet'}</span>
            </>
          )}
          <span className="ml-auto"><CountBadge n={c.unread} /></span>
        </span>
      </span>
    </div>
  );
};

const PresenceChip = ({ p, onClick }) => (
  <button type="button" onClick={onClick} data-testid="chat-presence-user-row"
    className="flex shrink-0 flex-col items-center gap-1" style={{ width: 60 }} title={p.name}>
    <span className="relative"><Avatar name={p.name} id={p.id} size={44} /><Dot online /></span>
    <span className="w-full truncate text-center text-[11px]" style={{ color: 'var(--n-ink-soft)' }}>{(p.name || '').split(' ')[0]}</span>
  </button>
);

export const ChatDock = ({ open, onClose, onUnread, onReserveChange }) => {
  const { principal } = useNotesAuth();
  const navigate = useNavigate();
  const me = principal?.id;

  const [users, setUsers] = useState([]);
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null); // conversation object
  const [messages, setMessages] = useState([]);
  const [membersMap, setMembersMap] = useState({}); // user_id -> {last_read_at,last_delivered_at,name}
  const [draft, setDraft] = useState('');
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [hasMore, setHasMore] = useState(false);       // older messages exist beyond what's loaded
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [newCount, setNewCount] = useState(0);         // new messages arrived while scrolled up
  const [groupOpen, setGroupOpen] = useState(false);
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [pollOpen, setPollOpen] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [membersOpen, setMembersOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // conversation pending hard-delete
  const [deleting, setDeleting] = useState(false);
  const [deleteErr, setDeleteErr] = useState('');
  const [convQuery, setConvQuery] = useState('');
  const [presenceOpen, setPresenceOpen] = useState(false); // collapsed by default
  const [typing, setTyping] = useState({}); // convId -> { userId: name }
  const [width, setWidth] = useState(() => Number(localStorage.getItem('chat_dock_w')) || 440);
  // Docked mode (tablet + laptop only) — pins the panel to the right edge with
  // no backdrop; the shell reflows its content by the reserved width. Persisted
  // so the dock survives switching between Notes & Tasks (separate route trees).
  const [docked, setDocked] = useState(() => {
    try { return localStorage.getItem('chat_docked') === '1'; } catch { return false; }
  });
  const toggleDock = useCallback(() => {
    setDocked((d) => { const nx = !d; try { localStorage.setItem('chat_docked', nx ? '1' : '0'); } catch { /* noop */ } return nx; });
  }, []);
  // Per-conversation chat background (falls back to the user's default). Both
  // are per-user, this-device preferences persisted in localStorage.
  const [bgDefault, setBgDefaultState] = useState(getDefaultBg);
  const [bgMap, setBgMap] = useState(readBgMap);
  const setDefaultBg = useCallback((key) => { setBgDefaultState(key); persistDefaultBg(key); }, []);
  const setConvBg = useCallback((convId, key) => {
    if (!convId) return;
    setBgMap((prev) => { const next = { ...prev, [convId]: key }; writeBgMap(next); return next; });
  }, []);

  const activeRef = useRef(null); activeRef.current = active;
  const messagesRef = useRef([]); messagesRef.current = messages;
  const hasMoreRef = useRef(false); hasMoreRef.current = hasMore;
  const loadingOlderRef = useRef(false);
  const openRef = useRef(open); openRef.current = open;
  const replyToRef = useRef(null); replyToRef.current = replyTo;
  const scrollRef = useRef(null);
  const wsRef = useRef(null);
  const didConnectRef = useRef(false);
  const handlerRef = useRef(null);
  const typingTimers = useRef({});
  const typingSentRef = useRef(0);

  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;
  const isDark = typeof document !== 'undefined' && !!document.querySelector('.notes-scope.dark');

  const totalUnread = useMemo(() => convs.reduce((s, c) => s + (c.unread || 0), 0), [convs]);
  useEffect(() => { onUnread?.(totalUnread); }, [totalUnread, onUnread]);

  const dmUserIds = useMemo(() => new Set(convs.filter((c) => c.type === 'dm' && c.other_user_id).map((c) => c.other_user_id)), [convs]);
  const presenceStrip = useMemo(
    () => users.filter((u) => u.online && !dmUserIds.has(u.id)),
    [users, dmUserIds],
  );
  // Frontend is authoritative for "mine": WS broadcasts one payload to every
  // member, so the server's `mine` is computed for the sender only. Deriving it
  // from sender_id === me here keeps alignment + read-state correct for everyone.
  const normalizedMessages = useMemo(
    () => messages.map((m) => {
      const mine = !!(m.sender_id && me && m.sender_id === me);
      const reactions = (m.reactions || []).map((r) => ({
        ...r, mine: Array.isArray(r.user_ids) ? r.user_ids.includes(me) : !!r.mine,
      }));
      let poll = m.poll;
      if (poll && Array.isArray(poll.options)) {
        poll = { ...poll, options: poll.options.map((o) => ({
          ...o, voted: Array.isArray(o.voter_ids) ? o.voter_ids.includes(me) : !!o.voted,
        })) };
      }
      return { ...m, mine, reactions, poll };
    }),
    [messages, me],
  );

  const loadConversations = useCallback(async () => {
    try { const { data } = await notesApi.get('/notes/chat/conversations'); setConvs(sortConvs(data.conversations || [])); } catch { /* ignore */ }
  }, []);
  const loadUsers = useCallback(async () => {
    try { const { data } = await notesApi.get('/notes/chat/users'); setUsers(data.users || []); } catch { /* ignore */ }
  }, []);

  // Initial + on-open load. Conversations load always (for the badge).
  useEffect(() => { loadConversations(); }, [loadConversations]);
  useEffect(() => { if (open) { loadConversations(); loadUsers(); } }, [open, loadConversations, loadUsers]);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => { const el = scrollRef.current; if (el) el.scrollTop = el.scrollHeight; });
  }, []);

  // Are we within a short distance of the newest message? Used to decide
  // whether an incoming message should auto-scroll (only when already at the
  // bottom) so reading older history is never interrupted.
  const isNearBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return true;
    return (el.scrollHeight - el.scrollTop - el.clientHeight) < 140;
  }, []);

  const markRead = useCallback(async (cid) => {
    try {
      await notesApi.post(`/notes/chat/conversations/${cid}/read`, {});
      const now = new Date().toISOString();
      setConvs((prev) => prev.map((c) => (c.id === cid ? { ...c, unread: 0 } : c)));
      if (activeRef.current?.id === cid && me) {
        setMembersMap((prev) => ({ ...prev, [me]: { ...(prev[me] || {}), last_read_at: now, last_delivered_at: now } }));
      }
    } catch { /* ignore */ }
  }, [me]);

  const PAGE_SIZE = 30;

  const openConversation = useCallback(async (conv) => {
    setActive(conv); setMessages([]); setLoadingMsgs(true); setHasMore(false); setNewCount(0); setDraft(''); setReplyTo(null);
    try {
      // Load only the latest page first for a fast open; older messages are
      // fetched lazily as the user scrolls up (see loadOlder).
      const { data } = await notesApi.get(`/notes/chat/conversations/${conv.id}/messages?limit=${PAGE_SIZE}`);
      setMessages(data.messages || []);
      setHasMore(!!data.has_more);
      const mm = {}; (data.members || []).forEach((m) => { mm[m.user_id] = m; });
      setMembersMap(mm);
      scrollToBottom();
      markRead(conv.id);
    } catch { /* ignore */ } finally { setLoadingMsgs(false); }
  }, [markRead, scrollToBottom]);

  // Resync after a WS reconnect or when the tab/app returns to the foreground.
  // Mobile OSes tear the socket down when backgrounded, so any read-receipt /
  // message events fired while away are missed — this pulls fresh state (which
  // updates the conversation-list ticks + the open thread) without a refresh.
  const resyncChat = useCallback(() => {
    loadConversations();
    const conv = activeRef.current;
    if (!conv) return;
    notesApi.get(`/notes/chat/conversations/${conv.id}/messages?limit=${PAGE_SIZE}`).then(({ data }) => {
      const mm = {}; (data.members || []).forEach((m) => { mm[m.user_id] = m; });
      setMembersMap(mm);
      setMessages((prev) => {
        const ids = new Set(prev.map((x) => x.id));
        const extra = (data.messages || []).filter((x) => !ids.has(x.id));
        return extra.length ? [...prev, ...extra] : prev;
      });
    }).catch(() => { /* ignore */ });
  }, [loadConversations]);
  const resyncRef = useRef(null);
  useEffect(() => { resyncRef.current = resyncChat; }, [resyncChat]);

  // Fetch the next older page and prepend it, keeping the scroll position
  // pinned so the view doesn't jump while reading history.
  const loadOlder = useCallback(async () => {
    const conv = activeRef.current;
    const first = messagesRef.current[0];
    if (!conv || !first || loadingOlderRef.current || !hasMoreRef.current) return;
    loadingOlderRef.current = true;
    setLoadingOlder(true);
    const el = scrollRef.current;
    const prevHeight = el ? el.scrollHeight : 0;
    const prevTop = el ? el.scrollTop : 0;
    try {
      const { data } = await notesApi.get(`/notes/chat/conversations/${conv.id}/messages?before=${encodeURIComponent(first.created_at)}&limit=${PAGE_SIZE}`);
      const older = data.messages || [];
      setHasMore(!!data.has_more);
      if (older.length) {
        setMessages((prev) => {
          const ids = new Set(prev.map((m) => m.id));
          return [...older.filter((m) => !ids.has(m.id)), ...prev];
        });
        // Restore the scroll offset after the prepend renders.
        requestAnimationFrame(() => {
          const e2 = scrollRef.current;
          if (e2) e2.scrollTop = prevTop + (e2.scrollHeight - prevHeight);
        });
      }
    } catch { /* ignore */ } finally {
      loadingOlderRef.current = false;
      setLoadingOlder(false);
    }
  }, []);

  const openDm = useCallback(async (userId) => {
    try {
      const { data } = await notesApi.post('/notes/chat/conversations/dm', { user_id: userId });
      setConvs((prev) => (prev.some((c) => c.id === data.id) ? prev : [data, ...prev]));
      openConversation(data);
    } catch { /* ignore */ }
  }, [openConversation]);

  const bumpConvPreview = useCallback((convId, preview, createdAt) => {
    setConvs((prev) => {
      const upd = prev.map((c) => (c.id === convId ? { ...c, preview, last_message_at: createdAt, last_sender_id: me } : c));
      return sortConvs(upd);
    });
  }, [me]);

  // Pin / unpin a conversation for the current user (optimistic, resorts instantly).
  const togglePin = useCallback(async (conv) => {
    const next = !conv.pinned;
    setConvs((prev) => sortConvs(prev.map((c) => (c.id === conv.id ? { ...c, pinned: next } : c))));
    try {
      await notesApi.post(`/notes/chat/conversations/${conv.id}/pin`, { pinned: next });
    } catch {
      setConvs((prev) => sortConvs(prev.map((c) => (c.id === conv.id ? { ...c, pinned: !next } : c))));
    }
  }, []);

  // Hard-delete an entire conversation for everyone (DMs: either party; groups: admin/creator).
  const canDeleteConv = useCallback((c) => {
    if (!c) return false;
    if (c.type === 'dm') return true;
    return c.created_by === me;
  }, [me]);
  const requestDelete = useCallback((c) => { setDeleteErr(''); setConfirmDelete(c || null); }, []);
  const doDeleteConversation = useCallback(async () => {
    if (!confirmDelete) return;
    const conv = confirmDelete;
    setDeleting(true); setDeleteErr('');
    try {
      await notesApi.delete(`/notes/chat/conversations/${conv.id}`);
      setConvs((prev) => prev.filter((c) => c.id !== conv.id));
      setActive((a) => (a && a.id === conv.id ? null : a));
      setMembersOpen(false);
      setConfirmDelete(null);
    } catch (e) {
      setDeleteErr(e?.response?.data?.detail || 'Could not delete this chat. Please try again.');
    } finally {
      setDeleting(false);
    }
  }, [confirmDelete]);

  const send = useCallback(async () => {
    const text = draft.trim();
    const conv = activeRef.current;
    if (!text || !conv) return;
    const reply = replyToRef.current;
    setDraft(''); setReplyTo(null);
    try {
      const body = { text };
      if (reply) body.reply_to = { id: reply.id, text: reply.text, sender_name: reply.sender_name, kind: reply.kind };
      const { data } = await notesApi.post(`/notes/chat/conversations/${conv.id}/messages`, body);
      setMessages((prev) => (prev.some((m) => m.id === data.id) ? prev : [...prev, data]));
      bumpConvPreview(conv.id, text, data.created_at);
      scrollToBottom();
    } catch { setDraft(text); if (reply) setReplyTo(reply); }
  }, [draft, bumpConvPreview, scrollToBottom]);

  const sendCard = useCallback(async (item) => {
    const conv = activeRef.current;
    if (!conv || !item) return;
    setShareOpen(false);
    try {
      const { data } = await notesApi.post(`/notes/chat/conversations/${conv.id}/messages`, { kind: 'card', card: { type: item.type, id: item.id } });
      setMessages((prev) => (prev.some((m) => m.id === data.id) ? prev : [...prev, data]));
      bumpConvPreview(conv.id, `${item.type === 'task' ? '📋 ' : '📝 '}${item.title}`, data.created_at);
      scrollToBottom();
    } catch { /* the picker only lists shareable items, so this should not happen */ }
  }, [bumpConvPreview, scrollToBottom]);

  const reactToMessage = useCallback(async (m, emoji) => {
    const conv = activeRef.current;
    if (!conv || !m || !emoji) return;
    try {
      const { data } = await notesApi.post(`/notes/chat/conversations/${conv.id}/messages/${m.id}/react`, { emoji });
      setMessages((prev) => prev.map((x) => (x.id === m.id ? data : x)));
    } catch { /* ignore */ }
  }, []);

  const votePoll = useCallback(async (m, optionId) => {
    const conv = activeRef.current;
    if (!conv || !m || !optionId) return;
    try {
      const { data } = await notesApi.post(`/notes/chat/conversations/${conv.id}/messages/${m.id}/vote`, { option_id: optionId });
      setMessages((prev) => prev.map((x) => (x.id === m.id ? data : x)));
    } catch { /* ignore */ }
  }, []);

  const closePoll = useCallback(async (m) => {
    const conv = activeRef.current;
    if (!conv || !m) return;
    try {
      const { data } = await notesApi.post(`/notes/chat/conversations/${conv.id}/messages/${m.id}/poll/close`, {});
      setMessages((prev) => prev.map((x) => (x.id === m.id ? data : x)));
    } catch { /* ignore */ }
  }, []);

  const createPoll = useCallback(async ({ question, options, multi }) => {
    const conv = activeRef.current;
    if (!conv) return;
    setPollOpen(false);
    try {
      const { data } = await notesApi.post(`/notes/chat/conversations/${conv.id}/poll`, { question, options, multi });
      setMessages((prev) => (prev.some((m) => m.id === data.id) ? prev : [...prev, data]));
      bumpConvPreview(conv.id, `📊 ${question}`, data.created_at);
      scrollToBottom();
    } catch { /* ignore */ }
  }, [bumpConvPreview, scrollToBottom]);

  const deleteMessage = useCallback(async (m) => {
    const conv = activeRef.current;
    if (!conv || !m) return;
    try {
      const { data } = await notesApi.delete(`/notes/chat/conversations/${conv.id}/messages/${m.id}`);
      setMessages((prev) => prev.map((x) => (x.id === m.id ? data : x)));
      loadConversations();
    } catch { /* ignore */ }
  }, [loadConversations]);

  const startReply = useCallback((m) => {
    const preview = m.kind === 'card' ? (m.card?.title || 'Shared item') : (m.text || '');
    setReplyTo({ id: m.id, text: preview, sender_name: m.mine ? 'You' : (m.sender_name || 'User'), kind: m.kind || 'text' });
  }, []);

  const openCardItem = useCallback((card) => {
    if (!card?.id) return;
    onClose?.();
    if (card.type === 'task') navigate(`/notes/tasks?task=${card.id}`);
    else navigate(`/notes?note=${card.id}`);
  }, [navigate, onClose]);

  // ---- group admin: rename / avatar, add already exists, remove, leave ----
  const updateConversation = useCallback(async (patch) => {
    const conv = activeRef.current;
    if (!conv) return;
    try {
      const { data } = await notesApi.patch(`/notes/chat/conversations/${conv.id}`, patch);
      setActive((a) => (a && a.id === data.id ? { ...a, ...data } : a));
      setConvs((prev) => prev.map((c) => (c.id === data.id ? { ...c, ...data } : c)));
    } catch { /* ignore */ }
  }, []);

  const removeMember = useCallback(async (uid) => {
    const conv = activeRef.current;
    if (!conv) return;
    try {
      await notesApi.delete(`/notes/chat/conversations/${conv.id}/members/${uid}`);
      const { data } = await notesApi.get('/notes/chat/conversations');
      const fresh = (data.conversations || []).find((c) => c.id === conv.id);
      setConvs(data.conversations || []);
      if (fresh) setActive((a) => (a && a.id === fresh.id ? { ...a, ...fresh } : a));
    } catch { /* ignore */ }
  }, []);

  const leaveGroup = useCallback(async () => {
    const conv = activeRef.current;
    if (!conv) return;
    try { await notesApi.post(`/notes/chat/conversations/${conv.id}/leave`, {}); } catch { /* ignore */ }
    setMembersOpen(false);
    setActive(null);
    setConvs((prev) => prev.filter((c) => c.id !== conv.id));
  }, []);

  // ---- typing indicator ----
  const bumpTyping = useCallback((cid, uid, name, isTyping) => {
    const key = `${cid}:${uid}`;
    if (typingTimers.current[key]) { clearTimeout(typingTimers.current[key]); delete typingTimers.current[key]; }
    setTyping((prev) => {
      const conv = { ...(prev[cid] || {}) };
      if (isTyping) conv[uid] = name; else delete conv[uid];
      return { ...prev, [cid]: conv };
    });
    if (isTyping) {
      typingTimers.current[key] = setTimeout(() => {
        setTyping((prev) => { const conv = { ...(prev[cid] || {}) }; delete conv[uid]; return { ...prev, [cid]: conv }; });
        delete typingTimers.current[key];
      }, 5000);
    }
  }, []);

  const sendTyping = useCallback((isTyping) => {
    const conv = activeRef.current;
    const ws = wsRef.current;
    if (!conv || !ws || ws.readyState !== 1) return;
    const now = Date.now();
    if (isTyping) { if (now - typingSentRef.current < 2000) return; typingSentRef.current = now; } else { typingSentRef.current = 0; }
    try { ws.send(JSON.stringify({ type: 'typing', conversation_id: conv.id, is_typing: isTyping })); } catch { /* noop */ }
  }, []);

  // ---- WebSocket: presence + live delivery ----
  const handleWs = useCallback((msg) => {
    const t = msg.type;
    if (t === 'chat_message') {
      const cid = msg.conversation_id; const m = msg.message;
      const isActive = activeRef.current?.id === cid;
      // Server sends one payload to all members with `mine` set for the sender;
      // recompute locally so receivers correctly treat it as incoming.
      const mineMsg = !!(m.sender_id && me && m.sender_id === me);
      if (m.sender_id) bumpTyping(cid, m.sender_id, '', false);
      if (isActive) {
        const stick = mineMsg || isNearBottom();
        setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
        if (stick) { scrollToBottom(); setNewCount(0); }
        else { setNewCount((n) => n + 1); }   // keep reading position; surface a "new" pill
        if (!mineMsg && openRef.current) markRead(cid);
      }
      setConvs((prev) => {
        const found = prev.find((c) => c.id === cid);
        const bumpUnread = !mineMsg && !(isActive && openRef.current);
        if (!found) { loadConversations(); return prev; }
        const upd = prev.map((c) => (c.id === cid
          ? { ...c, preview: m.deleted ? 'Message deleted' : (m.kind === 'card' ? (m.card?.title || 'Shared item') : m.text), last_message_at: m.created_at, last_sender_id: m.sender_id, unread: (c.unread || 0) + (bumpUnread ? 1 : 0),
              members: mineMsg ? (c.members || []).map((mem) => (mem.user_id !== me && mem.online ? { ...mem, last_delivered_at: m.created_at } : mem)) : (c.members || []) }
          : c));
        return sortConvs(upd);
      });
    } else if (t === 'chat_message_update') {
      const cid = msg.conversation_id; const m = msg.message;
      if (activeRef.current?.id === cid) {
        setMessages((prev) => prev.map((x) => (x.id === m.id ? m : x)));
      }
      loadConversations();
    } else if (t === 'chat_typing') {
      bumpTyping(msg.conversation_id, msg.user_id, msg.name, msg.is_typing);
    } else if (t === 'chat_read') {
      if (activeRef.current?.id === msg.conversation_id) {
        setMembersMap((prev) => ({ ...prev, [msg.user_id]: { ...(prev[msg.user_id] || {}), last_read_at: msg.read_at, last_delivered_at: msg.read_at } }));
      }
      // Keep the conversation-list receipt (ticks) live even when the thread is closed.
      setConvs((prev) => prev.map((c) => (c.id === msg.conversation_id
        ? { ...c, members: (c.members || []).map((mem) => (mem.user_id === msg.user_id ? { ...mem, last_read_at: msg.read_at, last_delivered_at: msg.read_at } : mem)) }
        : c)));
    } else if (t === 'chat_deleted') {
      // Someone hard-deleted the whole conversation — drop it everywhere.
      const cid = msg.conversation_id;
      setConvs((prev) => prev.filter((c) => c.id !== cid));
      setActive((a) => (a && a.id === cid ? null : a));
      setConfirmDelete((cd) => (cd && cd.id === cid ? null : cd));
      if (activeRef.current?.id === cid) setMembersOpen(false);
    } else if (t === 'chat_presence') {
      setUsers((prev) => prev.map((u) => (u.id === msg.user_id ? { ...u, online: msg.online, last_seen: msg.last_seen || u.last_seen } : u)));
      setConvs((prev) => prev.map((c) => (c.other_user_id === msg.user_id ? { ...c, online: msg.online, last_seen: msg.last_seen || c.last_seen } : c)));
      setActive((a) => (a && a.other_user_id === msg.user_id ? { ...a, online: msg.online, last_seen: msg.last_seen || a.last_seen } : a));
    } else if (t === 'chat_members') {
      if (msg.removed_user_id && msg.removed_user_id === me) {
        setConvs((prev) => prev.filter((c) => c.id !== msg.conversation_id));
        setActive((a) => (a && a.id === msg.conversation_id ? null : a));
        setMembersOpen(false);
        return;
      }
      notesApi.get('/notes/chat/conversations').then(({ data }) => {
        setConvs(data.conversations || []);
        const fresh = (data.conversations || []).find((c) => c.id === msg.conversation_id);
        if (fresh && activeRef.current?.id === msg.conversation_id) openConversation(fresh);
      }).catch(() => { /* ignore */ });
    }
  }, [loadConversations, markRead, openConversation, scrollToBottom, isNearBottom, me, bumpTyping]);
  useEffect(() => { handlerRef.current = handleWs; }, [handleWs]);

  useEffect(() => {
    const token = localStorage.getItem('notes_token') || localStorage.getItem('inv_token');
    if (!token) return undefined;
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/^http/i, 'ws');
    const url = `${base}/api/notes/chat/ws?token=${encodeURIComponent(token)}`;
    let closed = false; let pingIv = null; let reconnect = null;
    const connect = () => {
      let ws; try { ws = new WebSocket(url); } catch { return; }
      wsRef.current = ws;
      ws.onopen = () => {
        pingIv = setInterval(() => { try { if (ws.readyState === 1) ws.send(JSON.stringify({ type: 'ping' })); } catch { /* noop */ } }, 25000);
        // On a RECONNECT (not the very first open), pull fresh state so ticks /
        // messages that changed while the socket was down are not left stale.
        if (didConnectRef.current) resyncRef.current?.();
        didConnectRef.current = true;
      };
      ws.onmessage = (e) => { try { handlerRef.current?.(JSON.parse(e.data)); } catch { /* noop */ } };
      ws.onclose = () => { if (pingIv) clearInterval(pingIv); if (!closed) reconnect = setTimeout(connect, 2500); };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
    };
    connect();
    return () => { closed = true; if (pingIv) clearInterval(pingIv); if (reconnect) clearTimeout(reconnect); try { wsRef.current?.close(); } catch { /* noop */ } };
  }, []);

  // Foreground resync: when the tab/app becomes visible again (e.g. returning
  // from a backgrounded mobile browser), immediately refetch so read-receipt
  // ticks and previews are current even before the socket finishes reconnecting.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') resyncRef.current?.(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => { document.removeEventListener('visibilitychange', onVisible); window.removeEventListener('focus', onVisible); };
  }, []);

  // ---- read-state helper for a bubble ----
  const isRead = useCallback((m) => {
    if (m.mine) {
      const others = Object.entries(membersMap).filter(([uid]) => uid !== m.sender_id);
      if (others.length === 0) return false;
      return others.every(([, mem]) => mem.last_read_at && mem.last_read_at >= m.created_at);
    }
    const mine = membersMap[me];
    return !!(mine && mine.last_read_at && mine.last_read_at >= m.created_at);
  }, [membersMap, me]);

  // ---- 3-state receipt (sent/delivered/read) for a bubble I sent ----
  const readState = useCallback((m) => {
    if (!m.mine) return null;
    const list = Object.entries(membersMap).map(([uid, mem]) => ({ user_id: uid, ...mem }));
    return computeReceipt(list, m.sender_id, m.created_at);
  }, [membersMap]);

  // ---- resize handle (desktop + laptop + tablet, mouse + touch) ----
  const startResize = useCallback((e) => {
    e.preventDefault();
    const getX = (ev) => (ev.clientX != null ? ev.clientX : (ev.touches && ev.touches[0] ? ev.touches[0].clientX : null));
    const move = (ev) => {
      const vw = window.innerWidth;
      const x = getX(ev);
      if (x == null) return;
      if (ev.cancelable) ev.preventDefault();
      setWidth(Math.max(340, Math.min(vw - 60, vw - x)));
    };
    const up = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      document.removeEventListener('pointercancel', up);
      document.body.style.userSelect = '';
      setWidth((w) => { localStorage.setItem('chat_dock_w', String(w)); return w; });
    };
    document.body.style.userSelect = 'none';
    document.addEventListener('pointermove', move, { passive: false });
    document.addEventListener('pointerup', up);
    document.addEventListener('pointercancel', up);
  }, []);

  // ---- docked reserve width: tell the shell how much right padding to add so
  // the app content reflows beside the docked panel (never under it). Only in
  // docked mode on tablet/laptop; 0 otherwise. Reset to 0 on unmount (e.g. when
  // Maintenance Lockdown removes the chat entirely).
  const reservePx = (open && docked && !isMobile)
    ? Math.round(Math.min(width, (typeof window !== 'undefined' ? window.innerWidth - 40 : width)))
    : 0;
  useEffect(() => { onReserveChange?.(reservePx); }, [reservePx, onReserveChange]);
  useEffect(() => () => { onReserveChange?.(0); }, [onReserveChange]);

  if (!open) return null;

  const panelWidth = isMobile ? '100%' : Math.min(width, (typeof window !== 'undefined' ? window.innerWidth - 40 : width));
  // Docked panels (and mobile) are single-pane: show the conversation list OR a
  // single thread, never both side by side.
  const singlePane = isMobile || docked;
  // Premium hairline between conversations: a soft golden line in light mode,
  // a subtle grey line in dark mode, faded at both ends so it never looks harsh.
  const convDivider = isDark
    ? 'linear-gradient(90deg, transparent, color-mix(in srgb, var(--n-ink) 15%, transparent) 15%, color-mix(in srgb, var(--n-ink) 15%, transparent) 85%, transparent)'
    : 'linear-gradient(90deg, transparent, color-mix(in srgb, var(--n-accent) 42%, transparent) 15%, color-mix(in srgb, var(--n-accent) 42%, transparent) 85%, transparent)';

  const cq = convQuery.trim().toLowerCase();
  const filteredConvs = cq ? convs.filter((c) => (c.name || '').toLowerCase().includes(cq)) : convs;

  const threadProps = {
    conv: active, messages: normalizedMessages, loading: loadingMsgs, isRead, readState, me,
    scrollRef, draft, setDraft, onSend: send,
    replyTo, onReply: startReply, onCancelReply: () => setReplyTo(null),
    onDelete: deleteMessage, onOpenCard: openCardItem, onOpenShare: () => setShareOpen(true),
    onOpenMembers: () => setMembersOpen(true), members: membersMap,
    onTyping: sendTyping, typingNames: Object.values((active && typing[active.id]) || {}).filter(Boolean),
    onReact: reactToMessage, onVote: votePoll, onClosePoll: closePoll,
    onOpenPoll: () => setPollOpen(true), isDark,
    canDelete: canDeleteConv(active), onDeleteConversation: () => requestDelete(active),
    hasMore, loadingOlder, onLoadOlder: loadOlder,
    newCount, onSeenLatest: () => setNewCount(0),
    bg: (active && bgMap[active.id]) || bgDefault,
    bgDefault, onSetBg: (k) => setConvBg(active?.id, k), onSetDefaultBg: setDefaultBg,
  };

  return (
    <>
      {!docked && (
        <div className="fixed inset-0 z-[300]" style={{ background: 'rgba(0,0,0,.28)' }} onClick={onClose} data-testid="chat-backdrop" />
      )}
      <section
        className="fixed inset-y-0 right-0 z-[310] flex flex-col"
        style={{ width: panelWidth, maxWidth: '100%', background: 'var(--n-surface)', boxShadow: 'var(--n-shadow)' }}
        data-testid="chat-panel" data-docked={docked ? 'true' : 'false'}>
        {!isMobile && (
          <div onPointerDown={startResize} data-testid="chat-panel-resize-handle" title="Drag to resize"
            className="group/resize absolute left-0 top-0 z-20 flex h-full w-3 cursor-col-resize items-center justify-center"
            style={{ touchAction: 'none' }}
            onMouseEnter={(e) => { const p = e.currentTarget.firstChild; if (p) { p.style.background = 'var(--n-accent)'; p.style.opacity = '1'; } }}
            onMouseLeave={(e) => { const p = e.currentTarget.firstChild; if (p) { p.style.background = 'var(--n-border)'; p.style.opacity = '0.7'; } }}>
            <span className="h-16 w-1 rounded-full" style={{ background: 'var(--n-border)', opacity: 0.7 }} />
          </div>
        )}

        {/* When a conversation is open in single-pane (mobile or docked), show
            the full-panel thread. */}
        {active && singlePane ? (
          <ThreadView key={active.id} {...threadProps} onBack={() => setActive(null)} onClose={onClose} mobile={isMobile} />
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center gap-2 border-b px-3 py-3" style={{ borderColor: 'var(--n-line)' }}>
              <MessageSquare size={18} style={{ color: 'var(--n-accent)' }} />
              <span className="font-display text-[16px] font-semibold" style={{ color: 'var(--n-ink)' }}>Chat</span>
              <span className="ml-auto flex items-center gap-1.5">
                <button type="button" onClick={() => setNewChatOpen(true)} data-testid="chat-new-chat-button"
                  className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[12.5px] font-semibold text-white transition-colors" style={{ background: 'var(--n-accent)' }}>
                  <SquarePen size={14} /> New chat
                </button>
                <button type="button" onClick={() => setGroupOpen(true)} data-testid="chat-new-group-button"
                  className="inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[12.5px] font-semibold transition-colors" style={{ borderColor: 'var(--n-border)', color: 'var(--n-ink-soft)' }}>
                  <Users size={14} /> Group
                </button>
                {!isMobile && (
                  <button type="button" onClick={toggleDock} data-testid="chat-dock-toggle"
                    title={docked ? 'Undock chat (float over the app)' : 'Dock chat to the right'} aria-pressed={docked}
                    className="grid h-9 w-9 place-items-center rounded-lg transition-colors"
                    style={{ color: docked ? 'var(--n-accent)' : 'var(--n-muted)', background: docked ? 'var(--n-accent-bg)' : 'transparent' }}>
                    {docked ? <PanelRightClose size={18} /> : <PanelRight size={18} />}
                  </button>
                )}
                <button type="button" onClick={onClose} data-testid="chat-close-button"
                  className="grid h-9 w-9 place-items-center rounded-lg" style={{ color: 'var(--n-muted)' }}><X size={18} /></button>
              </span>
            </div>

            {/* Presence strip — online users you don't yet have a conversation with.
                Collapsible, collapsed by default. */}
            {presenceStrip.length > 0 && (
              <div className="border-b px-3 py-2" style={{ borderColor: 'var(--n-line)' }}>
                <button type="button" onClick={() => setPresenceOpen((v) => !v)} data-testid="chat-presence-toggle"
                  className="flex w-full items-center gap-1.5">
                  {presenceOpen ? <ChevronDown size={14} style={{ color: 'var(--n-muted)' }} /> : <ChevronRight size={14} style={{ color: 'var(--n-muted)' }} />}
                  <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em]" style={{ color: 'var(--n-muted-2)' }}>Online now</span>
                  <span className="grid h-4 min-w-4 place-items-center rounded-full px-1 text-[10px] font-bold text-white" style={{ background: 'var(--n-success)' }}>{presenceStrip.length}</span>
                  {!presenceOpen && (
                    <span className="ml-1 flex -space-x-2">
                      {presenceStrip.slice(0, 4).map((p) => (
                        <span key={p.id} className="rounded-full ring-2" style={{ '--tw-ring-color': 'var(--n-surface)' }}><Avatar name={p.name} id={p.id} size={22} /></span>
                      ))}
                    </span>
                  )}
                  <Users size={13} className="ml-auto" style={{ color: 'var(--n-muted)' }} />
                </button>
                {presenceOpen && (
                  <div className="mt-2 flex gap-1.5 overflow-x-auto pb-1" data-testid="chat-presence-strip">
                    {presenceStrip.map((p) => <PresenceChip key={p.id} p={p} onClick={() => openDm(p.id)} />)}
                  </div>
                )}
              </div>
            )}

            <div className="flex min-h-0 flex-1">
              {/* Conversation list */}
              <div className={`${(active && !singlePane) ? 'w-[44%] border-r' : 'w-full'} flex shrink-0 flex-col overflow-hidden py-1`}
                style={{ borderColor: 'var(--n-line)' }} data-testid="chat-conversation-list">
                {convs.length > 0 && (
                  <div className="px-3 pb-1.5 pt-2">
                    <div className="flex items-center gap-2 rounded-xl border px-2.5 py-1.5" style={{ borderColor: 'var(--n-border)', background: 'var(--n-surface-2)' }}>
                      <Search size={14} style={{ color: 'var(--n-muted-2)' }} />
                      <input value={convQuery} onChange={(e) => setConvQuery(e.target.value)} placeholder="Search conversations…" data-testid="chat-conversation-search"
                        className="min-w-0 flex-1 bg-transparent outline-none" style={{ color: 'var(--n-ink)', fontSize: 16 }} />
                      {convQuery && (
                        <button type="button" onClick={() => setConvQuery('')} data-testid="chat-conversation-search-clear" className="grid h-5 w-5 place-items-center rounded" style={{ color: 'var(--n-muted)' }}><X size={13} /></button>
                      )}
                    </div>
                  </div>
                )}
                <div className="min-h-0 flex-1 overflow-y-auto">
                  {convs.length > 0 && (
                    <div className="px-3 pb-1 pt-1 text-[10.5px] font-semibold uppercase tracking-[0.14em]" style={{ color: 'var(--n-muted-2)' }}>Conversations</div>
                  )}
                  {convs.length === 0 ? (
                    <div className="flex flex-col items-center gap-3 px-4 py-12 text-center" data-testid="chat-empty-state">
                      <span className="grid h-12 w-12 place-items-center rounded-full" style={{ background: 'var(--n-canvas-2)' }}>
                        <MessageSquare size={22} style={{ color: 'var(--n-muted-2)' }} />
                      </span>
                      <span className="text-[12.5px]" style={{ color: 'var(--n-muted)' }}>No conversations yet.</span>
                      <button type="button" onClick={() => setNewChatOpen(true)} data-testid="chat-empty-new-chat-button"
                        className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-[12.5px] font-semibold text-white" style={{ background: 'var(--n-accent)' }}>
                        <SquarePen size={14} /> Start a new chat
                      </button>
                    </div>
                  ) : filteredConvs.length === 0 ? (
                    <div className="px-4 py-10 text-center text-[12.5px]" style={{ color: 'var(--n-muted)' }} data-testid="chat-conversation-no-match">
                      No conversations match “{convQuery}”.
                    </div>
                  ) : (
                    <div className="px-1">{filteredConvs.map((c, i) => {
                      const tn = Object.values(typing[c.id] || {}).filter(Boolean);
                      const typingLabel = tn.length
                        ? ((c.type === 'group' && tn[0]) ? `${String(tn[0]).split(' ')[0]} is typing…` : 'typing…')
                        : '';
                      return (
                        <React.Fragment key={c.id}>
                          <ConvRow c={c} active={active?.id === c.id} onClick={() => openConversation(c)} typingLabel={typingLabel} me={me} pinned={!!c.pinned} onTogglePin={togglePin} canDelete={canDeleteConv(c)} onRequestDelete={requestDelete} />
                          {i < filteredConvs.length - 1 && (
                            <div className="mx-3 h-px" style={{ background: convDivider }} data-testid="chat-conversation-divider" />
                          )}
                        </React.Fragment>
                      );
                    })}</div>
                  )}
                </div>
              </div>

              {/* Inline thread (desktop, undocked two-pane) */}
              {active && !singlePane && (
                <div className="flex min-w-0 flex-1 flex-col">
                  <ThreadView key={active.id} {...threadProps} onBack={() => setActive(null)} />
                </div>
              )}
              {!active && !singlePane && (
                <div className="hidden flex-1 flex-col items-center justify-center gap-2 md:flex" style={{ background: 'var(--n-canvas)' }}>
                  <MessageSquare size={34} style={{ color: 'var(--n-muted-2)' }} />
                  <span className="text-[13px]" style={{ color: 'var(--n-muted)' }}>Select a conversation</span>
                </div>
              )}
            </div>
          </>
        )}
      </section>

      {groupOpen && (
        <NewGroupDialog users={users} onClose={() => setGroupOpen(false)}
          onCreated={(conv) => { setGroupOpen(false); setConvs((prev) => [conv, ...prev]); openConversation(conv); loadUsers(); }} />
      )}

      {newChatOpen && (
        <NewChatDialog users={users} convs={convs} onClose={() => setNewChatOpen(false)}
          onPick={(uid) => { setNewChatOpen(false); openDm(uid); }} />
      )}

      {shareOpen && active && (
        <ShareDialog conv={active} onClose={() => setShareOpen(false)} onPick={sendCard} />
      )}

      {pollOpen && active && active.type === 'group' && (
        <PollDialog onClose={() => setPollOpen(false)} onCreate={createPoll} />
      )}

      {membersOpen && active && active.type === 'group' && (
        <MembersDialog conv={active} users={users} me={me} onClose={() => setMembersOpen(false)}
          onUpdate={updateConversation} onRemove={removeMember} onLeave={leaveGroup}
          onAdded={(updated) => {
            setActive((a) => (a && a.id === updated.id ? { ...a, ...updated } : a));
            setConvs((prev) => prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)));
            loadUsers();
          }} />
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-[400] flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="chat-delete-confirm">
          <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(3px)' }}
            onClick={() => { if (!deleting) setConfirmDelete(null); }} />
          <div className="relative w-full max-w-[340px] rounded-2xl border p-5 shadow-[var(--n-shadow)]"
            style={{ borderColor: 'var(--n-border)', background: 'var(--n-surface)' }}>
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full"
                style={{ background: 'color-mix(in srgb, var(--n-danger) 14%, transparent)', color: 'var(--n-danger)' }}>
                <AlertTriangle size={20} />
              </span>
              <div className="min-w-0">
                <h3 className="text-[15px] font-semibold" style={{ color: 'var(--n-ink)' }} data-testid="chat-delete-title">Delete chat?</h3>
                <p className="mt-1 text-[12.5px] leading-relaxed" style={{ color: 'var(--n-muted)' }}>
                  This permanently deletes <span className="font-semibold" style={{ color: 'var(--n-ink-soft)' }}>{confirmDelete.name}</span> and all of its messages for everyone. This can’t be undone.
                </p>
              </div>
            </div>
            {deleteErr && <p className="mt-3 text-[12px] font-medium" style={{ color: 'var(--n-danger)' }} data-testid="chat-delete-error">{deleteErr}</p>}
            <div className="mt-4 flex items-center justify-end gap-2">
              <button type="button" onClick={() => setConfirmDelete(null)} disabled={deleting} data-testid="chat-delete-cancel"
                className="rounded-lg px-3 py-1.5 text-[12.5px] font-semibold transition-colors disabled:opacity-50"
                style={{ color: 'var(--n-ink-soft)', background: 'var(--n-canvas-2)' }}>Cancel</button>
              <button type="button" onClick={doDeleteConversation} disabled={deleting} data-testid="chat-delete-confirm-button"
                className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12.5px] font-semibold text-white transition-opacity disabled:opacity-60"
                style={{ background: 'var(--n-danger)' }}>
                {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// -- Thread view (shared by desktop inline + mobile full-screen) ------------
const ThreadView = ({
  conv, messages, loading, isRead, readState, me, members, scrollRef, draft, setDraft, onSend, onBack, onClose, mobile,
  replyTo, onReply, onCancelReply, onDelete, onOpenCard, onOpenShare, onOpenMembers, onTyping, typingNames = [],
  onReact, onVote, onClosePoll, onOpenPoll, isDark,
  canDelete = false, onDeleteConversation,
  hasMore = false, loadingOlder = false, onLoadOlder,
  newCount = 0, onSeenLatest,
  bg = 'plain', bgDefault = 'plain', onSetBg, onSetDefaultBg,
}) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeMatch, setActiveMatch] = useState(0);
  const [menu, setMenu] = useState({ open: false, x: 0, y: 0, m: null });
  const [emoji, setEmoji] = useState({ open: false, mode: 'compose', m: null });
  const [bgOpen, setBgOpen] = useState(false);
  const isGroup = conv.type === 'group';

  // On opening a conversation, jump straight to the latest message so users
  // never have to scroll down through a long history. ThreadView is keyed by
  // conversation id (it remounts per conversation), so this fires once the
  // messages for THIS conversation have rendered. useLayoutEffect pins the
  // scroll before paint, so there's no visible jump from top to bottom.
  const didInitialScrollRef = useRef(false);
  useLayoutEffect(() => {
    if (didInitialScrollRef.current) return;
    if (loading || !messages.length) return;
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    didInitialScrollRef.current = true;
    // Re-pin after paint in case late layout (emoji/images/fonts) shifts height.
    requestAnimationFrame(() => { const e2 = scrollRef.current; if (e2) e2.scrollTop = e2.scrollHeight; });
  }, [loading, messages.length, scrollRef]);

  // Lazy-load older messages when the user scrolls near the top of the thread,
  // and surface a "jump to latest" affordance once they've scrolled up.
  const [showJump, setShowJump] = useState(false);
  const handleScroll = useCallback((e) => {
    const el = e.currentTarget;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowJump(dist > 240);
    if (dist < 80 && onSeenLatest) onSeenLatest();   // caught up — clear the "new" pill
    if (onLoadOlder && hasMore && !loadingOlder && el.scrollTop <= 64) onLoadOlder();
  }, [onLoadOlder, hasMore, loadingOlder, onSeenLatest]);
  const jumpToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    setShowJump(false);
    if (onSeenLatest) onSeenLatest();
  }, [scrollRef, onSeenLatest]);

  // Auto-grow the composer: long messages wrap + scroll vertically (never
  // horizontally), capped so the thread stays usable, then the textarea scrolls.
  const taRef = useRef(null);
  const COMPOSER_MAX_H = 132;
  const autoGrow = useCallback((el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, COMPOSER_MAX_H)}px`;
  }, []);
  useEffect(() => { autoGrow(taRef.current); }, [draft, autoGrow]);

  // Insert an emoji at the cursor; wrap the current selection with a formatting
  // marker (*, _, ~, `) for the toolbar buttons + Ctrl/Cmd+B / Ctrl/Cmd+I.
  const insertAtCursor = useCallback((snippet) => {
    const el = taRef.current;
    const s = draft || '';
    const start = el && el.selectionStart != null ? el.selectionStart : s.length;
    const end = el && el.selectionEnd != null ? el.selectionEnd : start;
    const next = s.slice(0, start) + snippet + s.slice(end);
    setDraft(next);
    if (onTyping) onTyping(next.trim().length > 0);
    requestAnimationFrame(() => {
      if (!el) return; el.focus();
      const p = start + snippet.length;
      try { el.setSelectionRange(p, p); } catch { /* noop */ }
      autoGrow(el);
    });
  }, [draft, autoGrow, setDraft, onTyping]);

  const wrapSelection = useCallback((marker) => {
    const el = taRef.current;
    const s = draft || '';
    const start = el && el.selectionStart != null ? el.selectionStart : s.length;
    const end = el && el.selectionEnd != null ? el.selectionEnd : start;
    const sel = s.slice(start, end) || 'text';
    const next = s.slice(0, start) + marker + sel + marker + s.slice(end);
    setDraft(next);
    requestAnimationFrame(() => {
      if (!el) return; el.focus();
      const a = start + marker.length; const b = a + sel.length;
      try { el.setSelectionRange(a, b); } catch { /* noop */ }
      autoGrow(el);
    });
  }, [draft, autoGrow, setDraft]);

  const onEmojiPick = useCallback((data) => {
    const ch = (data && data.emoji) || '';
    if (!ch) return;
    if (emoji.mode === 'react' && emoji.m) onReact(emoji.m, ch);
    else insertAtCursor(ch);
    setEmoji({ open: false, mode: 'compose', m: null });
  }, [emoji, onReact, insertAtCursor]);

  const openMenu = ({ x, y }, m) => {
    const mw = 200; const mh = m?.mine ? 210 : 172;
    const cx = Math.min(x, (typeof window !== 'undefined' ? window.innerWidth : x) - mw - 8);
    const cy = Math.min(y, (typeof window !== 'undefined' ? window.innerHeight : y) - mh - 8);
    setMenu({ open: true, x: Math.max(8, cx), y: Math.max(8, cy), m });
  };
  const closeMenu = () => setMenu((s) => ({ ...s, open: false }));
  const copyMsg = async (m) => {
    const txt = m.kind === 'card' ? (m.card?.title || '') : (m.text || '');
    try { await navigator.clipboard.writeText(txt); } catch { /* ignore */ }
  };

  const matches = useMemo(() => {
    const ql = query.trim().toLowerCase();
    if (!ql) return [];
    return messages.filter((m) => !m.deleted && (
      (m.text && m.text.toLowerCase().includes(ql))
      || (m.kind === 'card' && m.card?.title && m.card.title.toLowerCase().includes(ql))
    )).map((m) => m.id);
  }, [query, messages]);

  useEffect(() => { setActiveMatch(0); }, [query]);
  useEffect(() => {
    if (!matches.length) return;
    const id = matches[Math.min(activeMatch, matches.length - 1)];
    const el = scrollRef.current?.querySelector(`[data-mid="${id}"]`);
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [activeMatch, matches, scrollRef]);

  const gotoMatch = (dir) => setActiveMatch((i) => { const n = matches.length; if (!n) return 0; return (i + dir + n) % n; });
  const activeMatchId = matches.length ? matches[Math.min(activeMatch, matches.length - 1)] : null;
  const typingText = typingNames.length === 1 ? `${typingNames[0]} is typing…`
    : typingNames.length === 2 ? `${typingNames[0]} and ${typingNames[1]} are typing…`
    : typingNames.length > 2 ? 'Several people are typing…' : '';

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="flex shrink-0 items-center gap-2.5 border-b px-3 py-2.5" style={{ borderColor: 'var(--n-line)' }}>
        <button type="button" onClick={onBack} data-testid="chat-thread-back" className="grid h-8 w-8 place-items-center rounded-lg" style={{ color: 'var(--n-muted)' }}>
          {mobile ? <ArrowLeft size={20} /> : <ChevronLeft size={20} />}
        </button>
        <span className="relative"><Avatar name={conv.name} id={conv.other_user_id || conv.id} size={34} group={isGroup} avatar={conv.avatar} /><Dot online={conv.online} hidden={isGroup} /></span>
        {isGroup ? (
          <button type="button" onClick={onOpenMembers} data-testid="chat-group-header" className="min-w-0 rounded-lg px-1 py-0.5 text-left transition-colors"
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--n-canvas-2)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
            <span className="block truncate text-[14px] font-semibold" style={{ color: 'var(--n-ink)' }} data-testid="chat-thread-title">{conv.name}</span>
            {typingText ? (
              <span className="block truncate text-[11px] font-semibold n-typing-shimmer" data-testid="chat-typing-indicator">{typingText}</span>
            ) : (
              <span className="flex items-center gap-0.5 text-[11px]" style={{ color: 'var(--n-accent)' }}>
                {conv.member_count || (conv.member_ids || []).length} members <ChevronRight size={12} />
              </span>
            )}
          </button>
        ) : (
          <span className="min-w-0">
            <span className="block truncate text-[14px] font-semibold" style={{ color: 'var(--n-ink)' }} data-testid="chat-thread-title">{conv.name}</span>
            {typingText ? (
              <span className="block text-[11px] font-semibold n-typing-shimmer" data-testid="chat-typing-indicator">typing…</span>
            ) : (
              <span className="block text-[11px]" style={{ color: conv.online ? 'var(--n-success)' : 'var(--n-muted)' }}>
                {conv.online ? 'online' : fmtLastSeen(conv.last_seen)}
              </span>
            )}
          </span>
        )}
        <span className="ml-auto flex items-center gap-1">
          <div className="relative">
            <button type="button" onClick={() => setBgOpen((v) => !v)} data-testid="chat-bg-toggle" title="Chat background"
              className="grid h-8 w-8 place-items-center rounded-lg" style={{ color: bgOpen ? 'var(--n-accent)' : 'var(--n-muted)', background: bgOpen ? 'var(--n-accent-bg)' : 'transparent' }}>
              <Palette size={17} />
            </button>
            {bgOpen && (
              <>
                <div className="fixed inset-0 z-[329]" onClick={() => setBgOpen(false)} data-testid="chat-bg-backdrop" />
                <div className="absolute right-0 top-full z-[330] mt-1.5 w-64 rounded-xl border p-2.5 shadow-[var(--n-shadow)]"
                  style={{ borderColor: 'var(--n-border)', background: 'var(--n-surface)' }} data-testid="chat-bg-panel">
                  <div className="px-0.5 pb-1.5 text-[10.5px] font-semibold uppercase tracking-[0.12em]" style={{ color: 'var(--n-muted-2)' }}>Chat background</div>
                  <div className="grid grid-cols-2 gap-2">
                    {CHAT_BG_OPTIONS.map((o) => {
                      const selected = bg === o.key;
                      return (
                        <button key={o.key} type="button" onClick={() => onSetBg?.(o.key)} data-testid={`chat-bg-option-${o.key}`}
                          className="group/bg rounded-lg p-1 text-left transition-colors" aria-pressed={selected}
                          style={{ boxShadow: selected ? '0 0 0 2px var(--n-accent)' : '0 0 0 1px var(--n-border)' }}>
                          <span className="relative block h-11 w-full overflow-hidden rounded-md" style={chatBgSwatchStyle(o.key)}>
                            {selected && (
                              <span className="absolute right-1 top-1 grid h-4 w-4 place-items-center rounded-full text-white" style={{ background: 'var(--n-accent)' }}>
                                <CheckIcon size={11} />
                              </span>
                            )}
                          </span>
                          <span className="mt-1 block text-center text-[11px] font-medium" style={{ color: selected ? 'var(--n-accent)' : 'var(--n-ink-soft)' }}>{o.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-2.5 flex items-center justify-between gap-2 border-t pt-2" style={{ borderColor: 'var(--n-line)' }}>
                    {bgDefault === bg ? (
                      <span className="text-[10.5px] font-medium" style={{ color: 'var(--n-muted-2)' }} data-testid="chat-bg-default-note">This is your default</span>
                    ) : (
                      <span className="text-[10.5px]" style={{ color: 'var(--n-muted)' }}>New chats use your default</span>
                    )}
                    <button type="button" onClick={() => onSetDefaultBg?.(bg)} disabled={bgDefault === bg} data-testid="chat-bg-set-default"
                      className="shrink-0 rounded-lg px-2 py-1 text-[11px] font-semibold transition-colors disabled:opacity-40"
                      style={{ color: 'var(--n-accent)', background: 'var(--n-accent-bg)' }}>Set as default</button>
                  </div>
                </div>
              </>
            )}
          </div>
          <button type="button" onClick={() => setSearchOpen((v) => !v)} data-testid="chat-search-toggle"
            className="grid h-8 w-8 place-items-center rounded-lg" style={{ color: searchOpen ? 'var(--n-accent)' : 'var(--n-muted)', background: searchOpen ? 'var(--n-accent-bg)' : 'transparent' }}>
            <Search size={17} />
          </button>
          {canDelete && (
            <button type="button" onClick={onDeleteConversation} data-testid="chat-thread-delete" title="Delete chat" aria-label="Delete chat"
              className="grid h-8 w-8 place-items-center rounded-lg transition-colors" style={{ color: 'var(--n-muted)' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--n-danger)'; e.currentTarget.style.background = 'var(--n-canvas-2)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--n-muted)'; e.currentTarget.style.background = 'transparent'; }}>
              <Trash2 size={17} />
            </button>
          )}
          {mobile && onClose && (
            <button type="button" onClick={onClose} className="grid h-8 w-8 place-items-center rounded-lg" style={{ color: 'var(--n-muted)' }}><X size={18} /></button>
          )}
        </span>
      </div>

      {searchOpen && (
        <div className="flex shrink-0 items-center gap-2 border-b px-3 py-2" style={{ borderColor: 'var(--n-line)', background: 'var(--n-canvas)' }} data-testid="chat-search-bar">
          <Search size={14} style={{ color: 'var(--n-muted-2)' }} />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search this chat…" autoFocus data-testid="chat-search-input"
            onKeyDown={(e) => { if (e.key === 'Enter') gotoMatch(e.shiftKey ? -1 : 1); if (e.key === 'Escape') { setQuery(''); setSearchOpen(false); } }}
            className="min-w-0 flex-1 bg-transparent outline-none" style={{ color: 'var(--n-ink)', fontSize: 16 }} />
          <span className="shrink-0 text-[11px] tabular-nums" style={{ color: 'var(--n-muted)' }} data-testid="chat-search-count">
            {matches.length ? `${activeMatch + 1}/${matches.length}` : '0/0'}
          </span>
          <button type="button" onClick={() => gotoMatch(-1)} disabled={!matches.length} data-testid="chat-search-prev"
            className="grid h-7 w-7 place-items-center rounded-md disabled:opacity-40" style={{ color: 'var(--n-muted)' }}><ChevronUp size={16} /></button>
          <button type="button" onClick={() => gotoMatch(1)} disabled={!matches.length} data-testid="chat-search-next"
            className="grid h-7 w-7 place-items-center rounded-md disabled:opacity-40" style={{ color: 'var(--n-muted)' }}><ChevronDown size={16} /></button>
          <button type="button" onClick={() => { setQuery(''); setSearchOpen(false); }} data-testid="chat-search-close"
            className="grid h-7 w-7 place-items-center rounded-md" style={{ color: 'var(--n-muted)' }}><X size={15} /></button>
        </div>
      )}

      <div className="relative flex min-h-0 flex-1 flex-col">
      <div ref={scrollRef} onScroll={handleScroll} className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto overflow-x-hidden overscroll-y-contain p-3" style={chatBgStyle(bg)} data-testid="chat-message-list" data-bg={bg}>
        {!loading && messages.length > 0 && (hasMore || loadingOlder) && (
          <div className="flex justify-center py-1" data-testid="chat-older-loader">
            {loadingOlder
              ? <Loader2 className="animate-spin" size={16} style={{ color: 'var(--n-muted)' }} />
              : <span className="text-[11px]" style={{ color: 'var(--n-muted-2)' }}>Scroll up for older messages</span>}
          </div>
        )}
        {loading ? (
          <div className="grid flex-1 place-items-center"><Loader2 className="animate-spin" size={22} style={{ color: 'var(--n-accent)' }} /></div>
        ) : messages.length === 0 ? (
          <div className="grid flex-1 place-items-center text-[12.5px]" style={{ color: 'var(--n-muted)' }}>Say hello 👋</div>
        ) : messages.map((m, i) => {
          const prev = messages[i - 1];
          const showDate = m.created_at && (!prev || !sameCalDay(prev.created_at, m.created_at));
          const dateDivider = showDate ? (
            <div className="my-2 flex justify-center" data-testid="chat-date-divider">
              <span className="rounded-full border px-3 py-1 text-[11px] font-semibold shadow-[var(--n-shadow)]"
                style={{ background: 'var(--n-surface)', borderColor: 'var(--n-border)', color: 'var(--n-muted)' }}>
                {fmtDateDivider(m.created_at)}
              </span>
            </div>
          ) : null;
          if (m.kind === 'system') {
            return (
              <React.Fragment key={m.id}>
                {dateDivider}
                <div data-mid={m.id} className="my-1 flex justify-center" data-testid="chat-system-message">
                  <span className="rounded-full px-3 py-1 text-center text-[11px]" style={{ background: 'var(--n-canvas-2)', color: 'var(--n-muted)' }}>{m.text}</span>
                </div>
              </React.Fragment>
            );
          }
          const showSender = conv.type === 'group' && !m.mine && (!prev || prev.sender_id !== m.sender_id || prev.kind === 'system');
          return (
            <React.Fragment key={m.id}>
              {dateDivider}
              <div data-mid={m.id}>
                {showSender && <span className="mb-0.5 ml-1 block text-[11px] font-medium" style={{ color: 'var(--n-muted)' }}>{m.sender_name}</span>}
                <Bubble m={m} read={isRead(m)} state={readState(m)} query={searchOpen ? query : ''} activeMatch={activeMatchId === m.id}
                  isGroup={isGroup} members={members} me={me} onOpenCard={onOpenCard} onOpenMenu={openMenu}
                  onReact={onReact} onVote={onVote} onClosePoll={onClosePoll}
                  onOpenReactPicker={(mm) => setEmoji({ open: true, mode: 'react', m: mm })} />
              </div>
            </React.Fragment>
          );
        })}
        {!loading && typingText && (
          <div className="flex justify-start" data-testid="chat-typing-bubble">
            <span className="flex items-center gap-1 rounded-2xl border px-3 py-2.5" style={{ borderColor: 'var(--n-border)', background: 'var(--n-surface-2)', borderTopLeftRadius: 5 }}>
              {[0, 150, 300].map((d) => (
                <span key={d} className="h-1.5 w-1.5 animate-bounce rounded-full" style={{ background: 'var(--n-muted-2)', animationDelay: `${d}ms` }} />
              ))}
            </span>
          </div>
        )}
      </div>
        {(showJump || newCount > 0) && (
          <button type="button" onClick={jumpToBottom} data-testid="chat-jump-latest"
            className="absolute bottom-3 right-4 z-10 inline-flex items-center gap-1 rounded-full border py-1.5 pl-2.5 pr-3 text-[12px] font-semibold shadow-[var(--n-shadow)] transition-transform hover:scale-105"
            style={{ background: newCount > 0 ? 'var(--n-accent)' : 'var(--n-surface)', borderColor: newCount > 0 ? 'transparent' : 'var(--n-border)', color: newCount > 0 ? '#fff' : 'var(--n-ink-soft)' }}
            title="Jump to latest messages">
            <ChevronDown size={15} style={{ color: newCount > 0 ? '#fff' : 'var(--n-accent)' }} />
            {newCount > 0 ? `${newCount} new message${newCount > 1 ? 's' : ''}` : 'Latest'}
          </button>
        )}
      </div>

      {menu.open && (
        <>
          <div className="fixed inset-0 z-[330]" onClick={closeMenu} onContextMenu={(e) => { e.preventDefault(); closeMenu(); }} data-testid="chat-message-menu-backdrop" />
          <div className="fixed z-[331] w-[200px] overflow-hidden rounded-xl border py-1" data-testid="chat-message-menu"
            style={{ left: menu.x, top: menu.y, background: 'var(--n-surface)', borderColor: 'var(--n-border)', boxShadow: 'var(--n-shadow)' }}>
            {!menu.m?.deleted && (
              <div className="flex items-center justify-between px-2 py-1.5" data-testid="chat-menu-reactions">
                {QUICK_REACTIONS.map((e) => (
                  <button key={e} type="button" data-testid="chat-menu-reaction" onClick={() => { onReact(menu.m, e); closeMenu(); }}
                    className="grid h-8 w-8 place-items-center rounded-full text-[18px] leading-none transition-transform hover:scale-125">{e}</button>
                ))}
                <button type="button" data-testid="chat-menu-reaction-more" onClick={() => { const mm = menu.m; closeMenu(); setEmoji({ open: true, mode: 'react', m: mm }); }}
                  className="grid h-8 w-8 place-items-center rounded-full" style={{ color: 'var(--n-muted)' }}><Plus size={16} /></button>
              </div>
            )}
            {!menu.m?.deleted && <div className="my-1 h-px" style={{ background: 'var(--n-line)' }} />}
            <button type="button" data-testid="chat-menu-reply" onClick={() => { onReply(menu.m); closeMenu(); }}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[13px]" style={{ color: 'var(--n-ink)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--n-canvas-2)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
              <CornerUpLeft size={15} style={{ color: 'var(--n-muted)' }} /> Reply
            </button>
            <button type="button" data-testid="chat-menu-copy" onClick={() => { copyMsg(menu.m); closeMenu(); }}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[13px]" style={{ color: 'var(--n-ink)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--n-canvas-2)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
              <Copy size={15} style={{ color: 'var(--n-muted)' }} /> Copy text
            </button>
            {menu.m?.mine && !menu.m?.deleted && (
              <button type="button" data-testid="chat-menu-delete" onClick={() => { onDelete(menu.m); closeMenu(); }}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[13px]" style={{ color: 'var(--n-danger)' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--n-canvas-2)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                <Trash2 size={15} /> Delete
              </button>
            )}
          </div>
        </>
      )}

      <div className="relative shrink-0 border-t px-3 py-2.5" style={{ borderColor: 'var(--n-line)' }}>
        {replyTo && (
          <div className="mb-2 flex items-start gap-2 rounded-lg border-l-[3px] px-2 py-1.5" style={{ borderColor: 'var(--n-accent)', background: 'var(--n-surface-2)' }} data-testid="chat-reply-preview">
            <span className="min-w-0 flex-1">
              <span className="block text-[11px] font-semibold" style={{ color: 'var(--n-accent)' }}>Replying to {replyTo.sender_name}</span>
              <span className="block truncate text-[12px]" style={{ color: 'var(--n-muted)' }}>{replyTo.text || 'Shared item'}</span>
            </span>
            <button type="button" onClick={onCancelReply} data-testid="chat-reply-cancel" className="grid h-6 w-6 shrink-0 place-items-center rounded-md" style={{ color: 'var(--n-muted)' }}><X size={14} /></button>
          </div>
        )}

        {/* Toolbar — text formatting + actions. Wraps gracefully in a narrow
            docked panel so nothing ever gets squished off. */}
        <div className="mb-1.5 flex flex-wrap items-center gap-1" data-testid="chat-format-toolbar">
          {[
            { m: '*', Icon: Bold, tid: 'chat-format-bold', label: 'Bold  (Ctrl/⌘B)' },
            { m: '_', Icon: Italic, tid: 'chat-format-italic', label: 'Italic  (Ctrl/⌘I)' },
            { m: '~', Icon: Strikethrough, tid: 'chat-format-strike', label: 'Strikethrough' },
            { m: '`', Icon: Code, tid: 'chat-format-code', label: 'Inline code' },
          ].map(({ m, Icon, tid, label }) => (
            <button key={tid} type="button" data-testid={tid} title={label} onClick={() => wrapSelection(m)}
              className="grid h-8 w-8 shrink-0 place-items-center rounded-md border transition-colors" style={{ borderColor: 'var(--n-border)', color: 'var(--n-muted)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--n-canvas-2)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
              <Icon size={14} />
            </button>
          ))}
          <span className="mx-0.5 h-5 w-px shrink-0" style={{ background: 'var(--n-line)' }} aria-hidden="true" />
          <button type="button" data-testid="chat-emoji-button" title="Emoji"
            onClick={() => {
              if (emoji.open && emoji.mode === 'compose') { setEmoji({ open: false, mode: 'compose', m: null }); return; }
              if (mobile && taRef.current) taRef.current.blur();
              setEmoji({ open: true, mode: 'compose', m: null });
            }}
            className="grid h-8 w-8 shrink-0 place-items-center rounded-md border transition-colors"
            style={{ borderColor: 'var(--n-border)', color: emoji.open && emoji.mode === 'compose' ? 'var(--n-accent)' : 'var(--n-muted)' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--n-canvas-2)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
            <Smile size={16} />
          </button>
          <button type="button" onClick={onOpenShare} data-testid="chat-attach-button" title="Share a task or note"
            className="grid h-8 w-8 shrink-0 place-items-center rounded-md border transition-colors" style={{ borderColor: 'var(--n-border)', color: 'var(--n-muted)' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--n-canvas-2)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
            <Paperclip size={15} />
          </button>
          {isGroup && (
            <button type="button" onClick={onOpenPoll} data-testid="chat-poll-button" title="Create a poll"
              className="grid h-8 w-8 shrink-0 place-items-center rounded-md border transition-colors" style={{ borderColor: 'var(--n-border)', color: 'var(--n-muted)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--n-canvas-2)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
              <BarChart3 size={15} />
            </button>
          )}
        </div>

        {/* Input — textarea always spans the full width; Send sits inline on
            the right so the field can never be squeezed to nothing. */}
        <div className="flex items-end gap-2 rounded-xl border px-3 py-1.5" style={{ borderColor: 'var(--n-border)', background: 'var(--n-surface-2)' }}>
          <textarea ref={taRef} value={draft} rows={1}
            onChange={(e) => { setDraft(e.target.value); autoGrow(e.target); if (onTyping) onTyping(e.target.value.trim().length > 0); }}
            onBlur={() => { if (onTyping) onTyping(false); }}
            onKeyDown={(e) => {
              if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && (e.key === 'b' || e.key === 'B')) { e.preventDefault(); wrapSelection('*'); return; }
              if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && (e.key === 'i' || e.key === 'I')) { e.preventDefault(); wrapSelection('_'); return; }
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (onTyping) onTyping(false); onSend(); }
            }}
            placeholder="Message…" data-testid="chat-composer-input"
            className="min-w-0 flex-1 resize-none bg-transparent leading-snug outline-none"
            style={{ color: 'var(--n-ink)', fontSize: 16, maxHeight: COMPOSER_MAX_H, overflowY: 'auto' }} />
          <button type="button" onClick={() => { if (onTyping) onTyping(false); onSend(); }} disabled={!draft.trim()} data-testid="chat-composer-send-button"
            className="mb-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg text-white transition-opacity disabled:opacity-40" style={{ background: 'var(--n-accent)' }}>
            <Send size={16} />
          </button>
        </div>

        {emoji.open && (
          <>
            <div className="fixed inset-0 z-[330]" onClick={() => setEmoji({ open: false, mode: 'compose', m: null })} data-testid="chat-emoji-backdrop" />
            <div data-testid="chat-emoji-panel"
              className={mobile
                ? 'fixed inset-x-0 bottom-0 z-[331] rounded-t-2xl border-t p-2'
                : 'absolute bottom-full right-3 z-[331] mb-2 overflow-hidden rounded-2xl border'}
              style={{ borderColor: 'var(--n-border)', background: 'var(--n-surface)', boxShadow: 'var(--n-shadow)' }}>
              {emoji.mode === 'react' && (
                <div className="px-2 pb-1 pt-1 text-[11px] font-semibold" style={{ color: 'var(--n-muted)' }}>Pick a reaction</div>
              )}
              <EmojiPicker onEmojiClick={onEmojiPick}
                theme={isDark ? EmojiTheme.DARK : EmojiTheme.LIGHT}
                width={mobile ? '100%' : 320} height={mobile ? 360 : 400}
                previewConfig={{ showPreview: false }} lazyLoadEmojis autoFocusSearch={!mobile}
                searchPlaceHolder="Search emoji" />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// -- Create a poll (group chats) --------------------------------------------
const PollDialog = ({ onClose, onCreate }) => {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [multi, setMulti] = useState(false);
  const setOpt = (i, v) => setOptions((o) => o.map((x, idx) => (idx === i ? v : x)));
  const addOpt = () => setOptions((o) => (o.length >= 10 ? o : [...o, '']));
  const removeOpt = (i) => setOptions((o) => (o.length <= 2 ? o : o.filter((_, idx) => idx !== i)));
  const clean = options.map((o) => o.trim()).filter(Boolean);
  const canCreate = question.trim().length > 0 && clean.length >= 2;
  const submit = () => { if (canCreate) onCreate({ question: question.trim(), options: clean, multi }); };
  const inputCls = 'min-w-0 flex-1 rounded-xl border px-3 py-2 outline-none';
  const inputSty = { borderColor: 'var(--n-border)', background: 'var(--n-surface-2)', color: 'var(--n-ink)', fontSize: 16 };
  return (
    <div className="fixed inset-0 z-[320] grid place-items-center p-4" style={{ background: 'rgba(0,0,0,.28)' }} onClick={onClose} data-testid="chat-poll-dialog">
      <div onClick={(e) => e.stopPropagation()} className="relative flex w-full max-w-md flex-col rounded-2xl border" style={{ maxHeight: '86vh', borderColor: 'var(--n-border)', background: 'var(--n-surface)', boxShadow: 'var(--n-shadow)' }}>
        <div className="flex items-center gap-2 border-b px-4 py-3" style={{ borderColor: 'var(--n-line)' }}>
          <BarChart3 size={18} style={{ color: 'var(--n-accent)' }} />
          <span className="font-display text-[16px] font-semibold" style={{ color: 'var(--n-ink)' }}>Create a poll</span>
          <button type="button" onClick={onClose} className="ml-auto grid h-8 w-8 place-items-center rounded-lg" style={{ color: 'var(--n-muted)' }}><X size={18} /></button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--n-muted-2)' }}>Question</label>
          <input value={question} onChange={(e) => setQuestion(e.target.value)} autoFocus placeholder="Ask something…" data-testid="chat-poll-question-input"
            className={`mb-3 w-full rounded-xl border px-3 py-2 outline-none`} style={inputSty} />
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--n-muted-2)' }}>Options</label>
          <div className="flex flex-col gap-2">
            {options.map((o, i) => (
              <div key={i} className="flex items-center gap-2">
                <input value={o} onChange={(e) => setOpt(i, e.target.value)} placeholder={`Option ${i + 1}`} data-testid="chat-poll-option-input"
                  className={inputCls} style={inputSty} />
                {options.length > 2 && (
                  <button type="button" onClick={() => removeOpt(i)} data-testid="chat-poll-option-remove" className="grid h-9 w-9 shrink-0 place-items-center rounded-lg" style={{ color: 'var(--n-muted)' }}><X size={15} /></button>
                )}
              </div>
            ))}
          </div>
          {options.length < 10 && (
            <button type="button" onClick={addOpt} data-testid="chat-poll-add-option" className="mt-2 inline-flex items-center gap-1.5 text-[12.5px] font-semibold" style={{ color: 'var(--n-accent)' }}><Plus size={14} /> Add option</button>
          )}
          <label className="mt-4 flex cursor-pointer items-center gap-2 text-[13px]" style={{ color: 'var(--n-ink)' }} data-testid="chat-poll-multi-toggle">
            <input type="checkbox" checked={multi} onChange={(e) => setMulti(e.target.checked)} style={{ accentColor: 'var(--n-accent)', width: 16, height: 16 }} />
            Allow voters to pick multiple options
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 border-t px-4 py-3" style={{ borderColor: 'var(--n-line)' }}>
          <button type="button" onClick={onClose} className="rounded-xl px-3 py-2 text-[13px] font-semibold" style={{ color: 'var(--n-muted)' }}>Cancel</button>
          <button type="button" onClick={submit} disabled={!canCreate} data-testid="chat-poll-create"
            className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-[13px] font-semibold text-white disabled:opacity-40" style={{ background: 'var(--n-accent)' }}>
            <BarChart3 size={14} /> Create poll
          </button>
        </div>
      </div>
    </div>
  );
};

// -- Share task/note as a card ----------------------------------------------
const ShareDialog = ({ conv, onClose, onPick }) => {
  const [tab, setTab] = useState('task');
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    const handle = setTimeout(() => {
      notesApi.get(`/notes/chat/conversations/${conv.id}/shareable`, { params: { kind: tab, q } })
        .then(({ data }) => { if (!cancel) setItems(data.items || []); })
        .catch(() => { if (!cancel) setItems([]); })
        .finally(() => { if (!cancel) setLoading(false); });
    }, 180);
    return () => { cancel = true; clearTimeout(handle); };
  }, [conv.id, tab, q]);

  const TabBtn = ({ id, icon: Icon, label }) => (
    <button type="button" onClick={() => setTab(id)} data-testid={`chat-share-tab-${id}`}
      className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12.5px] font-semibold transition-colors"
      style={{ background: tab === id ? 'var(--n-accent)' : 'transparent', color: tab === id ? '#fff' : 'var(--n-ink-soft)', border: tab === id ? 'none' : '1px solid var(--n-border)' }}>
      <Icon size={14} /> {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-[340] grid place-items-center p-4" data-testid="chat-share-dialog" onClick={onClose}>
      <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,.5)' }} />
      <div onClick={(e) => e.stopPropagation()} className="relative flex w-full max-w-md flex-col rounded-2xl border" style={{ maxHeight: '82vh', borderColor: 'var(--n-border)', background: 'var(--n-surface)', boxShadow: 'var(--n-shadow)' }}>
        <div className="flex items-center gap-2 border-b px-4 py-3" style={{ borderColor: 'var(--n-line)' }}>
          <Paperclip size={17} style={{ color: 'var(--n-accent)' }} />
          <span className="font-display text-[16px] font-semibold" style={{ color: 'var(--n-ink)' }}>Share to chat</span>
          <button type="button" onClick={onClose} className="ml-auto grid h-8 w-8 place-items-center rounded-lg" style={{ color: 'var(--n-muted)' }}><X size={17} /></button>
        </div>
        <div className="px-4 pt-3">
          <div className="mb-2 flex items-center gap-1.5">
            <TabBtn id="task" icon={CheckSquare} label="Tasks" />
            <TabBtn id="note" icon={FileText} label="Notes" />
          </div>
          <div className="mb-1 flex items-center gap-2 rounded-xl border px-2.5 py-1.5" style={{ borderColor: 'var(--n-border)' }}>
            <Search size={14} style={{ color: 'var(--n-muted-2)' }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${tab === 'task' ? 'tasks' : 'notes'}…`} autoFocus data-testid="chat-share-search"
              className="min-w-0 flex-1 bg-transparent outline-none" style={{ color: 'var(--n-ink)', fontSize: 16 }} />
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3 pt-1">
          {loading ? (
            <div className="grid place-items-center py-10"><Loader2 className="animate-spin" size={20} style={{ color: 'var(--n-accent)' }} /></div>
          ) : items.length === 0 ? (
            <div className="px-4 py-8 text-center text-[12px] leading-relaxed" style={{ color: 'var(--n-muted)' }} data-testid="chat-share-empty">
              No shareable {tab === 'task' ? 'tasks' : 'notes'}.<br />
              You can only share items that <b>every member of this chat</b> already has access to.
            </div>
          ) : items.map((it) => (
            <button key={it.id} type="button" onClick={() => onPick(it)} data-testid="chat-share-item"
              className="flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors"
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--n-canvas-2)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
              <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg" style={{ background: 'var(--n-accent-bg)', color: 'var(--n-accent)' }}>
                {tab === 'task' ? <CheckSquare size={15} /> : <FileText size={15} />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13.5px] font-medium" style={{ color: 'var(--n-ink)' }}>{it.title}</span>
                {it.subtitle && <span className="block truncate text-[11.5px]" style={{ color: 'var(--n-muted)' }}>{it.subtitle}</span>}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// -- Group members: view + add people --------------------------------------
const GROUP_EMOJIS = ['💬', '🧪', '📦', '💎', '🏆', '🔧', '📋', '🎯', '🚀', '🔥', '⭐', '🛠️', '📌', '💼', '🧾', '🥇', '📣', '🧠'];

const MembersDialog = ({ conv, users, me, onClose, onUpdate, onRemove, onLeave, onAdded }) => {
  const [mode, setMode] = useState('view'); // 'view' | 'add' | 'edit'
  const [picked, setPicked] = useState(new Set());
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState(conv.name || '');
  const [avatar, setAvatar] = useState(conv.avatar || '');
  const isAdmin = conv.created_by === me;
  const memberList = conv.members || [];
  const memberIds = new Set(memberList.map((m) => m.user_id));
  const candidates = users.filter((u) => !memberIds.has(u.id) && u.name.toLowerCase().includes(q.toLowerCase()));
  const toggle = (id) => setPicked((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const confirmAdd = async () => {
    if (picked.size === 0 || busy) return;
    setBusy(true);
    try {
      const { data } = await notesApi.post(`/notes/chat/conversations/${conv.id}/members`, { user_ids: [...picked] });
      onAdded(data);
      setMode('view'); setPicked(new Set()); setQ('');
    } catch { /* ignore */ } finally { setBusy(false); }
  };
  const saveEdit = async () => {
    if (busy) return;
    const patch = {};
    if (name.trim() && name.trim() !== conv.name) patch.name = name.trim();
    if ((avatar || '') !== (conv.avatar || '')) patch.avatar = avatar || '';
    if (Object.keys(patch).length === 0) { setMode('view'); return; }
    setBusy(true);
    try { await onUpdate(patch); setMode('view'); } finally { setBusy(false); }
  };
  const title = mode === 'add' ? 'Add people' : mode === 'edit' ? 'Edit group' : conv.name;

  return (
    <div className="fixed inset-0 z-[340] grid place-items-center p-4" data-testid="chat-members-dialog" onClick={onClose}>
      <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,.5)' }} />
      <div onClick={(e) => e.stopPropagation()} className="relative flex w-full max-w-md flex-col rounded-2xl border" style={{ maxHeight: '82vh', borderColor: 'var(--n-border)', background: 'var(--n-surface)', boxShadow: 'var(--n-shadow)' }}>
        <div className="flex items-center gap-2 border-b px-4 py-3" style={{ borderColor: 'var(--n-line)' }}>
          {mode !== 'view' && (
            <button type="button" onClick={() => { setMode('view'); setPicked(new Set()); setQ(''); setName(conv.name || ''); setAvatar(conv.avatar || ''); }} className="grid h-8 w-8 place-items-center rounded-lg" style={{ color: 'var(--n-muted)' }}><ChevronLeft size={18} /></button>
          )}
          {mode === 'view' && <span className="relative"><Avatar name={conv.name} id={conv.id} size={28} group avatar={conv.avatar} /></span>}
          <span className="min-w-0 truncate font-display text-[16px] font-semibold" style={{ color: 'var(--n-ink)' }}>{title}</span>
          {mode === 'view' && <span className="text-[12px]" style={{ color: 'var(--n-muted)' }}>· {memberList.length}</span>}
          {mode === 'view' && isAdmin && (
            <button type="button" onClick={() => { setName(conv.name || ''); setAvatar(conv.avatar || ''); setMode('edit'); }} data-testid="chat-group-edit-button"
              className="grid h-8 w-8 place-items-center rounded-lg" style={{ color: 'var(--n-muted)' }} title="Edit group"><SquarePen size={15} /></button>
          )}
          <button type="button" onClick={onClose} className="ml-auto grid h-8 w-8 place-items-center rounded-lg" style={{ color: 'var(--n-muted)' }}><X size={17} /></button>
        </div>

        {mode === 'edit' ? (
          <>
            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
              <div className="mb-3 flex flex-col items-center gap-2">
                <Avatar name={name} id={conv.id} size={64} group avatar={avatar} />
                <span className="text-[11px]" style={{ color: 'var(--n-muted)' }}>Group photo</span>
              </div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--n-muted-2)' }}>Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Group name" data-testid="chat-group-name-edit"
                className="mb-3 w-full rounded-xl border px-3 py-2 outline-none" style={{ borderColor: 'var(--n-border)', background: 'var(--n-surface-2)', color: 'var(--n-ink)', fontSize: 16 }} />
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--n-muted-2)' }}>Photo (emoji)</label>
              <div className="flex flex-wrap gap-1.5" data-testid="chat-group-emoji-grid">
                <button type="button" onClick={() => setAvatar('')} data-testid="chat-group-emoji-none"
                  className="grid h-9 w-9 place-items-center rounded-lg border text-[12px]" style={{ borderColor: avatar === '' ? 'var(--n-accent)' : 'var(--n-border)', background: avatar === '' ? 'var(--n-accent-bg)' : 'transparent', color: 'var(--n-muted)' }}>
                  <Users size={16} />
                </button>
                {GROUP_EMOJIS.map((em) => (
                  <button key={em} type="button" onClick={() => setAvatar(em)} data-testid="chat-group-emoji-option"
                    className="grid h-9 w-9 place-items-center rounded-lg border text-[18px]" style={{ borderColor: avatar === em ? 'var(--n-accent)' : 'var(--n-border)', background: avatar === em ? 'var(--n-accent-bg)' : 'transparent' }}>
                    {em}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 border-t px-4 py-3" style={{ borderColor: 'var(--n-line)' }}>
              <button type="button" onClick={saveEdit} disabled={busy || !name.trim()} data-testid="chat-group-save-button"
                className="ml-auto inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-[13px] font-semibold text-white disabled:opacity-40" style={{ background: 'var(--n-accent)' }}>
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Save
              </button>
            </div>
          </>
        ) : mode === 'add' ? (
          <>
            <div className="px-4 pt-3">
              <div className="mb-1 flex items-center gap-2 rounded-xl border px-2.5 py-1.5" style={{ borderColor: 'var(--n-border)' }}>
                <Search size={14} style={{ color: 'var(--n-muted-2)' }} />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people…" autoFocus data-testid="chat-members-add-search"
                  className="min-w-0 flex-1 bg-transparent outline-none" style={{ color: 'var(--n-ink)', fontSize: 16 }} />
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2 pt-1">
              {candidates.length === 0 ? (
                <div className="px-4 py-8 text-center text-[12.5px]" style={{ color: 'var(--n-muted)' }}>Everyone is already in this group.</div>
              ) : candidates.map((u) => (
                <button key={u.id} type="button" onClick={() => toggle(u.id)} data-testid="chat-members-add-option"
                  className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left" style={{ background: picked.has(u.id) ? 'var(--n-accent-bg)' : 'transparent' }}>
                  <span className="relative"><Avatar name={u.name} id={u.id} size={32} /><Dot online={u.online} /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-medium" style={{ color: 'var(--n-ink)' }}>{u.name}</span>
                    <span className="block text-[11px]" style={{ color: 'var(--n-muted)' }}>{u.online ? 'online' : fmtLastSeen(u.last_seen)}</span>
                  </span>
                  <span className="grid h-5 w-5 place-items-center rounded-md border" style={{ borderColor: picked.has(u.id) ? 'var(--n-accent)' : 'var(--n-border)', background: picked.has(u.id) ? 'var(--n-accent)' : 'transparent' }}>
                    {picked.has(u.id) && <Check size={13} className="text-white" />}
                  </span>
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 border-t px-4 py-3" style={{ borderColor: 'var(--n-line)' }}>
              <span className="text-[12px]" style={{ color: 'var(--n-muted)' }}>{picked.size} selected</span>
              <button type="button" onClick={confirmAdd} disabled={picked.size === 0 || busy} data-testid="chat-members-add-confirm"
                className="ml-auto inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-[13px] font-semibold text-white disabled:opacity-40" style={{ background: 'var(--n-accent)' }}>
                {busy ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />} Add
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
              {memberList.map((m) => (
                <div key={m.user_id} className="group/mem flex items-center gap-2.5 rounded-lg px-2.5 py-2" data-testid="chat-member-row">
                  <span className="relative"><Avatar name={m.name} id={m.user_id} size={34} /><Dot online={m.online} /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13.5px] font-medium" style={{ color: 'var(--n-ink)' }}>
                      {m.name}{m.user_id === me ? ' (You)' : ''}
                    </span>
                    <span className="block text-[11px]" style={{ color: m.online ? 'var(--n-success)' : 'var(--n-muted)' }}>{m.online ? 'online' : 'offline'}</span>
                  </span>
                  {(m.user_id === conv.created_by || m.role === 'admin') && (
                    <span className="shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-semibold" style={{ background: 'var(--n-accent-bg)', color: 'var(--n-accent)' }}>Admin</span>
                  )}
                  {isAdmin && m.user_id !== me && (
                    <button type="button" onClick={() => onRemove(m.user_id)} data-testid="chat-member-remove"
                      className="shrink-0 rounded-md px-2 py-1 text-[11px] font-semibold opacity-0 transition-opacity group-hover/mem:opacity-100"
                      style={{ color: 'var(--n-danger)', background: 'var(--n-canvas-2)' }}>Remove</button>
                  )}
                </div>
              ))}
            </div>
            <div className="flex flex-col gap-2 border-t px-4 py-3" style={{ borderColor: 'var(--n-line)' }}>
              <button type="button" onClick={() => setMode('add')} data-testid="chat-members-add-button"
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl px-4 py-2 text-[13px] font-semibold text-white" style={{ background: 'var(--n-accent)' }}>
                <UserPlus size={15} /> Add people
              </button>
              <button type="button" onClick={onLeave} data-testid="chat-group-leave-button"
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl border px-4 py-2 text-[13px] font-semibold" style={{ borderColor: 'var(--n-border)', color: 'var(--n-danger)' }}>
                <ArrowLeft size={15} /> Leave group
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// -- New chat (DM) dialog ---------------------------------------------------
// Lets you start a 1-on-1 with ANY user — online or offline, with or without
// a prior conversation. This is the entry point for offline / no-history DMs
// (the presence strip only surfaces online people you have no chat with yet).
const NewChatDialog = ({ users, convs, onClose, onPick }) => {
  const [q, setQ] = useState('');
  const existing = useMemo(
    () => new Set((convs || []).filter((c) => c.type === 'dm' && c.other_user_id).map((c) => c.other_user_id)),
    [convs],
  );
  const term = q.trim().toLowerCase();
  const filtered = users.filter(
    (u) => u.name.toLowerCase().includes(term) || (u.email || '').toLowerCase().includes(term),
  );
  return (
    <div className="fixed inset-0 z-[340] grid place-items-center p-4" data-testid="chat-new-chat-dialog" onClick={onClose}>
      <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,.5)' }} />
      <div onClick={(e) => e.stopPropagation()} className="relative flex w-full max-w-md flex-col rounded-2xl border" style={{ maxHeight: '82vh', borderColor: 'var(--n-border)', background: 'var(--n-surface)', boxShadow: 'var(--n-shadow)' }}>
        <div className="flex items-center gap-2 border-b px-4 py-3" style={{ borderColor: 'var(--n-line)' }}>
          <SquarePen size={17} style={{ color: 'var(--n-accent)' }} />
          <span className="font-display text-[16px] font-semibold" style={{ color: 'var(--n-ink)' }}>New chat</span>
          <button type="button" onClick={onClose} className="ml-auto grid h-8 w-8 place-items-center rounded-lg" style={{ color: 'var(--n-muted)' }}><X size={17} /></button>
        </div>
        <div className="px-4 pt-3">
          <div className="mb-1 flex items-center gap-2 rounded-xl border px-2.5 py-1.5" style={{ borderColor: 'var(--n-border)' }}>
            <Search size={14} style={{ color: 'var(--n-muted-2)' }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people…" autoFocus data-testid="chat-new-chat-search"
              className="min-w-0 flex-1 bg-transparent outline-none" style={{ color: 'var(--n-ink)', fontSize: 16 }} />
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3 pt-1">
          {filtered.length === 0 ? (
            <div className="px-4 py-8 text-center text-[12.5px]" style={{ color: 'var(--n-muted)' }}>No people found.</div>
          ) : filtered.map((u) => (
            <button key={u.id} type="button" onClick={() => onPick(u.id)} data-testid="chat-new-chat-user-option"
              className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors"
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--n-canvas-2)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
              <span className="relative"><Avatar name={u.name} id={u.id} size={36} /><Dot online={u.online} /></span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13.5px] font-medium" style={{ color: 'var(--n-ink)' }}>{u.name}</span>
                <span className="block text-[11px]" style={{ color: u.online ? 'var(--n-success)' : 'var(--n-muted)' }}>{u.online ? 'online' : fmtLastSeen(u.last_seen)}</span>
              </span>
              {existing.has(u.id) && (
                <span className="shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-semibold" style={{ background: 'var(--n-canvas-2)', color: 'var(--n-muted)' }}>Open chat</span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// -- New group dialog -------------------------------------------------------
const NewGroupDialog = ({ users, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [picked, setPicked] = useState(new Set());
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const filtered = users.filter((u) => u.name.toLowerCase().includes(q.toLowerCase()));
  const toggle = (id) => setPicked((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const create = async () => {
    if (!name.trim() || picked.size === 0 || busy) return;
    setBusy(true);
    try { const { data } = await notesApi.post('/notes/chat/conversations/group', { name: name.trim(), member_ids: [...picked] }); onCreated(data); } catch { /* ignore */ } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[340] grid place-items-center p-4" data-testid="chat-new-group-dialog" onClick={onClose}>
      <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,.5)' }} />
      <div onClick={(e) => e.stopPropagation()} className="relative flex w-full max-w-md flex-col rounded-2xl border" style={{ maxHeight: '82vh', borderColor: 'var(--n-border)', background: 'var(--n-surface)', boxShadow: 'var(--n-shadow)' }}>
        <div className="flex items-center gap-2 border-b px-4 py-3" style={{ borderColor: 'var(--n-line)' }}>
          <UserPlus size={17} style={{ color: 'var(--n-accent)' }} />
          <span className="font-display text-[16px] font-semibold" style={{ color: 'var(--n-ink)' }}>New group</span>
          <button type="button" onClick={onClose} className="ml-auto grid h-8 w-8 place-items-center rounded-lg" style={{ color: 'var(--n-muted)' }}><X size={17} /></button>
        </div>
        <div className="px-4 pt-3">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Group name" autoFocus data-testid="chat-group-name-input"
            className="mb-2 w-full rounded-xl border px-3 py-2 outline-none" style={{ borderColor: 'var(--n-border)', background: 'var(--n-surface-2)', color: 'var(--n-ink)', fontSize: 16 }} />
          <div className="mb-2 flex items-center gap-2 rounded-xl border px-2.5 py-1.5" style={{ borderColor: 'var(--n-border)' }}>
            <Search size={14} style={{ color: 'var(--n-muted-2)' }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people…" data-testid="chat-group-search"
              className="min-w-0 flex-1 bg-transparent outline-none" style={{ color: 'var(--n-ink)', fontSize: 16 }} />
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
          {filtered.map((u) => (
            <button key={u.id} type="button" onClick={() => toggle(u.id)} data-testid="chat-group-member-option"
              className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left" style={{ background: picked.has(u.id) ? 'var(--n-accent-bg)' : 'transparent' }}>
              <span className="relative"><Avatar name={u.name} id={u.id} size={32} /><Dot online={u.online} /></span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-medium" style={{ color: 'var(--n-ink)' }}>{u.name}</span>
                <span className="block text-[11px]" style={{ color: 'var(--n-muted)' }}>{u.online ? 'online' : fmtLastSeen(u.last_seen)}</span>
              </span>
              <span className="grid h-5 w-5 place-items-center rounded-md border" style={{ borderColor: picked.has(u.id) ? 'var(--n-accent)' : 'var(--n-border)', background: picked.has(u.id) ? 'var(--n-accent)' : 'transparent' }}>
                {picked.has(u.id) && <Check size={13} className="text-white" />}
              </span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 border-t px-4 py-3" style={{ borderColor: 'var(--n-line)' }}>
          <span className="text-[12px]" style={{ color: 'var(--n-muted)' }}>{picked.size} selected</span>
          <button type="button" onClick={create} disabled={!name.trim() || picked.size === 0 || busy} data-testid="chat-group-create-button"
            className="ml-auto inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-[13px] font-semibold text-white disabled:opacity-40" style={{ background: 'var(--n-accent)' }}>
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Create
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatDock;
